<template>
  <div
    data-tour-id="samples-register-express-form"
    :class="{
      'express-sample-register--hydrating': isHydratingRegisteredRows,
    }"
  >
    <div
      v-if="isHydratingRegisteredRows"
      class="express-register-preloader-overlay"
    >
      <div class="preloader-card">
        <f7-preloader />
        <div class="preloader-text">
          {{ $t('loading') }}
        </div>
      </div>
    </div>

    <div v-else class="input-full">
      <div>
        <div class="card-content padding">
          <div class="form-container">
            <form>
              <DxForm
                ref="splRegisterForm"
                label-location="top"
                :col-count="3"
                :form-data="formData"
              >
                <DxFormGroupItem :col-count="3" :col-span="3">
                  <DxFormItem
                    data-field="CodPresupuesto"
                    editor-type="dxSelectBox"
                    :is-required="!expressMode"
                    :editor-options="{
                      dataSource: store,
                      displayExpr: 'CodOfferTitle',
                      valueExpr: 'CodPresupuesto',
                      searchEnabled: true,
                    }"
                  >
                    <DxFormLabel
                      :text="$t('samplesRegister.budget')"
                    />
                  </DxFormItem>

                  <DxFormItem
                    editor-type="dxTextBox"
                    data-field="Sampler"
                  >
                    <DxFormLabel
                      :text="$t('samplesRegister.sampler')"
                    />
                  </DxFormItem>

                  <DxFormItem
                    editor-type="dxDateBox"
                    data-field="SamplingDate"
                    :is-required="true"
                    :editor-options="{
                      type: 'datetime',
                      displayFormat:
                        $helpers.getDateFormat(company) + ' HH:mm',
                      applyValueMode: 'instantly',
                      elementAttr: {
                        class: 'samples-register-datetime-field',
                      },
                      dropDownOptions: {
                        wrapperAttr: {
                          class: 'samples-register-datetime-popup',
                        },
                      },
                    }"
                    :col-span="1"
                    :validation-rules="[
                      {
                        type: 'required',
                        message: $t('fillRequiredFields'),
                      },
                    ]"
                  >
                    <DxFormLabel
                      :text="
                        $t('samplesRegister.samplingDateAndTime')
                      "
                    />
                  </DxFormItem>
                </DxFormGroupItem>
              </DxForm>

              <div class="multi-samples-title">
                <div class="title">
                  {{ $t('samplesRegister.samplesToRegister') }}:
                </div>
                <!-- <div class="subtitle">
                  {{ $t('samplesRegister.multiRowsHelp') }}
                </div> -->
              </div>

              <div
                data-tour-id="samples-register-express-grid"
                :class="[
                  'express-grid-wrapper',
                  {
                    'express-grid-wrapper--hydrating':
                      isHydratingRegisteredRows,
                  },
                ]"
              >
                <DxDataGrid
                  :key="expressGridRenderKey"
                  v-if="!isHydratingRegisteredRows"
                  ref="samplesGrid"
                  class="custom-grid CustomExpressSamplesGrid"
                  :data-source="rows"
                  key-expr="id"
                  :show-borders="false"
                  :show-row-lines="false"
                  :show-column-lines="true"
                  :hover-state-enabled="true"
                  :column-auto-width="true"
                  :word-wrap-enabled="true"
                  @init-new-row="onInitNewRow"
                  @row-inserted="onRowInserted"
                  @row-removed="onRowRemoved"
                  @row-updated="onRowUpdated"
                  @row-prepared="onRowPrepared"
                  @editing-start="onEditingStart"
                  @editor-preparing="onEditorPreparing"
                  @toolbar-preparing="onToolbarPreparing($event)"
                >
                  <DxEditing
                    mode="cell"
                    :allow-updating="true"
                    :allow-adding="false"
                    :allow-deleting="false"
                    :use-icons="true"
                    :new-row-position="'last'"
                  />
                  <DxLoadPanel :enabled="false" />

                  <DxColumn
                    caption="#"
                    :width="25"
                    :allow-editing="false"
                    cell-template="idxTpl"
                    alignment="center"
                  />
                  <template #idxTpl="{ data: cell }">
                    <span v-if="cell && cell.rowType === 'data'">{{
                      cell.rowIndex + 1
                    }}</span>
                  </template>

                  <DxColumn
                    data-field="CodMuestra"
                    width="150"
                    :allow-editing="false"
                    :caption="$t('agronomySample.summary.sampleCode')"
                    alignment="center"
                  />

                  <DxColumn
                    v-if="hasMultipleBudgetCodesInInvoice"
                    data-field="CodPresupuesto"
                    width="170"
                    edit-cell-template="budgetEditor"
                    :caption="$t('samplesRegister.budget')"
                    alignment="center"
                  />
                  <template #budgetEditor="{ data: cell }">
                    <DxSelectBox
                      :value="cell.value"
                      :data-source="store"
                      display-expr="CodOfferTitle"
                      value-expr="CodPresupuesto"
                      :search-enabled="true"
                      :drop-down-options="{
                        width: 620,
                        maxHeight: 320,
                        wrapperAttr: {
                          class: 'express-budget-select-dropdown',
                        },
                      }"
                      :on-value-changed="
                        createRegisteredRowBudgetChangeHandler(cell)
                      "
                    />
                  </template>

                  <DxColumn
                    data-field="DescripcionMuestra"
                    width="250"
                    :caption="$t('samplesRegister.sampleDescription')"
                    header-cell-template="requiredHeaderTemplate"
                    alignment="center"
                  />

                  <DxColumn
                    data-field="FamiliaTM"
                    width="170"
                    :caption="$t('samplesRegister.family')"
                    header-cell-template="requiredHeaderTemplate"
                    :calculate-display-value="
                      (row) => getFamilyText(row)
                    "
                    edit-cell-template="familyEditor"
                    alignment="center"
                  />
                  <template #familyEditor="{ data: cell }">
                    <DxSelectBox
                      :value="cell.value"
                      :data-source="{
                        store: familiesFiltered,
                        sort: { selector: 'FamiliaTM', desc: false },
                      }"
                      display-expr="FamiliaTM"
                      value-expr="idFamiliaTM"
                      :search-enabled="true"
                      :disabled="
                        familiDisabled || isRowLocked(cell.data)
                      "
                      @value-changed="
                        (e) => onRowFamilyChanged(e.value, cell)
                      "
                    />
                  </template>

                  <DxColumn
                    data-field="Descripcion"
                    width="280"
                    :caption="$t('samplesRegister.sampleType')"
                    header-cell-template="requiredHeaderTemplate"
                    cell-template="sampleTypeDisplay"
                    edit-cell-template="sampleTypeEditor"
                    alignment="center"
                  />
                  <template #sampleTypeDisplay="{ data: cell }">
                    <span>{{ getSampleTypeText(cell.data) }}</span>
                  </template>
                  <template #sampleTypeEditor="{ data: cell }">
                    <DxSelectBox
                      :value="cell.value"
                      :data-source="getRowSampleTypes(cell.data)"
                      display-expr="Descripcion"
                      value-expr="idTipoMuestra"
                      :search-enabled="true"
                      :disabled="
                        isRowLocked(cell.data) ||
                        getRowUiProp(cell.data, 'sampleTypesDisabled')
                      "
                      @value-changed="
                        (e) => onRowSampleTypeChanged(e.value, cell)
                      "
                    />
                  </template>

                  <DxColumn
                    data-field="SubTipoMuestra"
                    :visible="newFieldsVisibility.SubTipoMuestra"
                    :caption="$t('samplesRegister.sampleSubtype')"
                    cell-template="subTypeDisplay"
                    edit-cell-template="subTypeEditor"
                    alignment="center"
                  />
                  <template #subTypeDisplay="{ data: cell }">
                    <span>{{ getSubTypeText(cell.data) }}</span>
                  </template>
                  <template #subTypeEditor="{ data: cell }">
                    <DxSelectBox
                      :value="cell.value"
                      :data-source="
                        getRowUiProp(cell.data, 'subSampleType') || []
                      "
                      display-expr="Descripcion"
                      value-expr="idSubTipoMuestra"
                      :search-enabled="true"
                      :drop-down-options="{
                        wrapperAttr: {
                          class: 'express-subtype-select-dropdown',
                        },
                      }"
                      :disabled="
                        isRowLocked(cell.data) ||
                        getRowUiProp(
                          cell.data,
                          'subSampleTypeVisible'
                        )
                      "
                      @value-changed="
                        (e) => onRowSubTypeChanged(e.value, cell)
                      "
                    />
                  </template>

                  <DxColumn
                    data-field="cloroResidual"
                    :visible="newFieldsVisibility.cloroResidual"
                    :caption="
                      $t('samplesRegister.freeResidualChlorine')
                    "
                    cell-template="chlorineDisplay"
                    edit-cell-template="chlorineEditor"
                    alignment="center"
                  />
                  <template #chlorineDisplay="{ data: cell }">
                    <span>{{
                      getRowUiProp(cell.data, 'chlorineDisabled')
                        ? cell.data.cloroResidual
                        : ''
                    }}</span>
                  </template>
                  <template #chlorineEditor="{ data: cell }">
                    <DxNumberBox
                      :value="cell.value"
                      :min="0"
                      :max="10"
                      :step="0.1"
                      format="#0.0"
                      :show-spin-buttons="true"
                      :disabled="
                        isRowLocked(cell.data) ||
                        !getRowUiProp(cell.data, 'chlorineDisabled')
                      "
                      @value-changed="
                        (e) =>
                          onRowFieldChanged(
                            'cloroResidual',
                            e.value,
                            cell
                          )
                      "
                    />
                  </template>

                  <DxColumn
                    data-field="Articulo"
                    :caption="$t('samplesRegister.analysisType')"
                    header-cell-template="requiredHeaderTemplate"
                    :calculate-display-value="
                      (row) => getAnalysisText(row)
                    "
                    edit-cell-template="analysisEditor"
                    alignment="center"
                    :min-width="260"
                  />
                  <template #requiredHeaderTemplate="{ data }">
                    <span>
                      {{ data.column.caption }}
                      <span class="required-header-asterisk">*</span>
                    </span>
                  </template>
                  <template #analysisEditor="{ data: cell }">
                    <DxSelectBox
                      :value="cell.value"
                      :data-source="
                        getRowArticlesBySubFamily(cell.data)
                      "
                      display-expr="AtDescrip"
                      value-expr="CodArticulo"
                      :search-enabled="true"
                      :drop-down-options="{
                        width: 620,
                        maxHeight: 320,
                        wrapperAttr: {
                          class: 'express-analysis-select-dropdown',
                        },
                      }"
                      :disabled="
                        isRowLocked(cell.data) ||
                        getRowUiProp(
                          cell.data,
                          'analysisTypeDisabled'
                        )
                      "
                      @value-changed="
                        (e) => onRowAnalysisChanged(e.value, cell)
                      "
                    />
                  </template>

                  <DxColumn
                    data-field="lote"
                    :visible="newFieldsVisibility.lote"
                    :caption="$t('samplesRegister.lot') || 'Lote'"
                    alignment="center"
                    :editor-options="{
                      maxLength: 50,
                    }"
                  />

                  <DxColumn
                    data-field="cma"
                    :visible="newFieldsVisibility.cma"
                    :caption="$t('samplesRegister.cmaEnabled')"
                    :allow-editing="false"
                    cell-template="cmaDisplay"
                    alignment="center"
                  />
                  <template #cmaDisplay="{ data: cell }">
                    <DxCheckBox
                      v-if="getRowUiProp(cell.data, 'checkCma')"
                      :value="
                        Boolean(getResolvedRowData(cell.data).cma)
                      "
                      :disabled="isRowLocked(cell.data)"
                      @value-changed="
                        (e) => onRowFieldChanged('cma', e.value, cell)
                      "
                    />
                    <span v-else>{{ $t('notApplicable') }}</span>
                  </template>

                  <DxColumn
                    data-field="Legislacion"
                    :visible="newFieldsVisibility.Legislacion"
                    :caption="$t('samplesRegister.legislation')"
                    :allow-editing="false"
                    cell-template="legislationDisplay"
                    alignment="center"
                  />
                  <template #legislationDisplay="{ data: cell }">
                    <DxSelectBox
                      :value="
                        getResolvedRowData(cell.data).Legislacion
                      "
                      :data-source="legislations"
                      display-expr="Legislacion"
                      value-expr="idLegislacion"
                      :search-enabled="true"
                      :disabled="
                        isRowLocked(cell.data) ||
                        !getRowUiProp(cell.data, 'legislationVisible')
                      "
                      :placeholder="
                        getRowUiProp(cell.data, 'legislationVisible')
                          ? ''
                          : $t('notApplicable')
                      "
                      :show-drop-down-button="
                        Boolean(
                          getRowUiProp(
                            cell.data,
                            'legislationVisible'
                          )
                        )
                      "
                      :input-attr="{
                        'aria-label': $t(
                          'samplesRegister.legislation'
                        ),
                      }"
                      @value-changed="
                        (e) =>
                          onRowFieldChanged(
                            'Legislacion',
                            e.value,
                            cell
                          )
                      "
                    />
                  </template>

                  <DxColumn
                    data-field="automaticSamplingPoint"
                    :visible="
                      newFieldsVisibility.automaticSamplingPoint
                    "
                    :caption="
                      $t('samplesRegister.automaticSamplingPoint')
                    "
                    cell-template="automaticSamplingPointDisplay"
                    edit-cell-template="automaticSamplingPointEditor"
                    alignment="center"
                  />
                  <template
                    #automaticSamplingPointDisplay="{ data: cell }"
                  >
                    <span>{{
                      isSamplingPointSectionVisible(cell.data)
                        ? getBooleanMark(
                            getAutomaticSamplingPointValue(cell.data)
                          )
                        : $t('notApplicable')
                    }}</span>
                  </template>
                  <template
                    #automaticSamplingPointEditor="{ data: cell }"
                  >
                    <DxCheckBox
                      v-if="isSamplingPointSectionVisible(cell.data)"
                      :value="
                        Boolean(
                          getAutomaticSamplingPointValue(cell.data)
                        )
                      "
                      :disabled="isRowLocked(cell.data)"
                      @value-changed="
                        (e) =>
                          onRowAutomaticSamplingPointChanged(
                            e.value,
                            cell
                          )
                      "
                    />
                    <span v-else>{{ $t('notApplicable') }}</span>
                  </template>

                  <DxColumn
                    data-field="FincaLiteral"
                    :visible="newFieldsVisibility.farmPoint"
                    :caption="$t('samplesRegister.farm')"
                    cell-template="farmPointDisplay"
                    edit-cell-template="farmPointEditor"
                    alignment="center"
                  />
                  <template #farmPointDisplay="{ data: cell }">
                    <span>{{
                      isSamplingPointSectionVisible(cell.data)
                        ? getFarmText(cell.data)
                        : ''
                    }}</span>
                  </template>
                  <template #farmPointEditor="{ data: cell }">
                    <DxSelectBox
                      v-if="
                        isSamplingPointSectionVisible(cell.data) &&
                        getAutomaticSamplingPointValue(cell.data)
                      "
                      :value="getResolvedRowData(cell.data).Parcelas"
                      :data-source="farmsWithParcels"
                      display-expr="Finca"
                      value-expr="idFinca"
                      :search-enabled="true"
                      :drop-down-options="{
                        width: 620,
                        maxHeight: 320,
                        wrapperAttr: {
                          class: 'express-farmPoint-select-dropdown',
                        },
                      }"
                      :disabled="isRowLocked(cell.data)"
                      @value-changed="
                        (e) => onRowFarmChanged(e.value, cell)
                      "
                    />
                    <DxTextBox
                      v-else-if="
                        isSamplingPointSectionVisible(cell.data) &&
                        !getAutomaticSamplingPointValue(cell.data)
                      "
                      :value="
                        getResolvedRowData(cell.data).FincaLiteral
                      "
                      :disabled="isRowLocked(cell.data)"
                      @value-changed="
                        (e) =>
                          onRowFieldChanged(
                            'FincaLiteral',
                            e.value,
                            cell
                          )
                      "
                    />
                  </template>

                  <DxColumn
                    data-field="ParcelaLiteral"
                    :visible="newFieldsVisibility.plotPoint"
                    :caption="$t('samplesRegister.plot')"
                    cell-template="plotPointDisplay"
                    edit-cell-template="plotPointEditor"
                    alignment="center"
                  />
                  <template #plotPointDisplay="{ data: cell }">
                    <span>{{
                      isSamplingPointSectionVisible(cell.data)
                        ? getPlotText(cell.data)
                        : ''
                    }}</span>
                  </template>
                  <template #plotPointEditor="{ data: cell }">
                    <DxSelectBox
                      v-if="
                        isSamplingPointSectionVisible(cell.data) &&
                        getAutomaticSamplingPointValue(cell.data)
                      "
                      :value="getResolvedRowData(cell.data).Parcela"
                      :data-source="getRowParcels(cell.data)"
                      display-expr="Parcela"
                      value-expr="idParcela"
                      :search-enabled="true"
                      :drop-down-options="{
                        width: 620,
                        maxHeight: 320,
                        wrapperAttr: {
                          class:
                            'express-parcelPoint-select-dropdown',
                        },
                      }"
                      :disabled="isRowLocked(cell.data)"
                      @value-changed="
                        (e) => onRowPlotChanged(e.value, cell)
                      "
                    />
                    <DxTextBox
                      v-else-if="
                        isSamplingPointSectionVisible(cell.data) &&
                        !getAutomaticSamplingPointValue(cell.data)
                      "
                      :value="
                        getResolvedRowData(cell.data).ParcelaLiteral
                      "
                      :disabled="isRowLocked(cell.data)"
                      @value-changed="
                        (e) =>
                          onRowFieldChanged(
                            'ParcelaLiteral',
                            e.value,
                            cell
                          )
                      "
                    />
                  </template>

                  <DxColumn
                    data-field="idNivelPunto"
                    :visible="
                      newFieldsVisibility.samplingPointCoordinate
                    "
                    :caption="$t('samplesRegister.pointCoordinate')"
                    cell-template="levelPointDisplay"
                    edit-cell-template="levelPointEditor"
                    alignment="center"
                  />
                  <template #levelPointDisplay="{ data: cell }">
                    <span>{{
                      getLevelPointDisplayText(cell.data)
                    }}</span>
                  </template>
                  <template #levelPointEditor="{ data: cell }">
                    <DxSelectBox
                      v-if="isLevelPointSelectionAvailable(cell.data)"
                      :value="
                        getResolvedRowData(cell.data).idNivelPunto
                      "
                      :data-source="getRowLevelPoints(cell.data)"
                      :display-expr="getLevelPointOptionText"
                      value-expr="idNivelPunto"
                      :search-enabled="true"
                      :drop-down-options="{
                        width: 620,
                        maxHeight: 320,
                        wrapperAttr: {
                          class: 'express-levelPoint-select-dropdown',
                        },
                      }"
                      :disabled="isRowLocked(cell.data)"
                      @value-changed="
                        (e) => onRowLevelPointChanged(e.value, cell)
                      "
                    />
                    <span v-else>{{
                      getLevelPointEmptyStateText(cell.data)
                    }}</span>
                  </template>

                  <DxColumn
                    data-field="lmr"
                    :visible="newFieldsVisibility.lmr"
                    :caption="$t('samplesRegister.lmrEnabled')"
                    cell-template="lmrDisplay"
                    edit-cell-template="lmrEditor"
                    alignment="center"
                  />
                  <template #lmrDisplay="{ data: cell }">
                    <span>{{
                      isLmrAnalysisSelected(cell.data)
                        ? getBooleanMark(
                            getResolvedRowData(cell.data).lmr
                          )
                        : $t('notApplicable')
                    }}</span>
                  </template>
                  <template #lmrEditor="{ data: cell }">
                    <DxCheckBox
                      v-if="isLmrAnalysisSelected(cell.data)"
                      :value="
                        Boolean(getResolvedRowData(cell.data).lmr)
                      "
                      :disabled="isRowLocked(cell.data)"
                      @value-changed="
                        (e) => onRowFieldChanged('lmr', e.value, cell)
                      "
                    />
                    <span v-else>{{ $t('notApplicable') }}</span>
                  </template>

                  <DxColumn
                    data-field="trazaCultivo"
                    :visible="newFieldsVisibility.trazaCultivo"
                    :caption="$t('samplesRegister.trace')"
                    cell-template="traceDisplay"
                    edit-cell-template="traceEditor"
                    alignment="center"
                  />
                  <template #traceDisplay="{ data: cell }">
                    <span>{{
                      isLmrAnalysisSelected(cell.data)
                        ? getBooleanMark(
                            getResolvedRowData(cell.data).trazaCultivo
                          )
                        : $t('notApplicable')
                    }}</span>
                  </template>
                  <template #traceEditor="{ data: cell }">
                    <DxCheckBox
                      v-if="isLmrAnalysisSelected(cell.data)"
                      :value="
                        Boolean(
                          getResolvedRowData(cell.data).trazaCultivo
                        )
                      "
                      :disabled="isRowLocked(cell.data)"
                      @value-changed="
                        (e) =>
                          onRowFieldChanged(
                            'trazaCultivo',
                            e.value,
                            cell
                          )
                      "
                    />
                    <span v-else>{{ $t('notApplicable') }}</span>
                  </template>

                  <DxColumn
                    data-field="CultivoOrganico"
                    :visible="newFieldsVisibility.CultivoOrganico"
                    :caption="$t('samplesRegister.organicFarming')"
                    cell-template="organicDisplay"
                    edit-cell-template="organicEditor"
                    alignment="center"
                  />
                  <template #organicDisplay="{ data: cell }">
                    <span>{{
                      isLmrAnalysisSelected(cell.data)
                        ? getBooleanMark(
                            getResolvedRowData(cell.data)
                              .CultivoOrganico
                          )
                        : $t('notApplicable')
                    }}</span>
                  </template>
                  <template #organicEditor="{ data: cell }">
                    <DxCheckBox
                      v-if="isLmrAnalysisSelected(cell.data)"
                      :value="
                        Boolean(
                          getResolvedRowData(cell.data)
                            .CultivoOrganico
                        )
                      "
                      :disabled="isRowLocked(cell.data)"
                      @value-changed="
                        (e) =>
                          onRowFieldChanged(
                            'CultivoOrganico',
                            e.value,
                            cell
                          )
                      "
                    />
                    <span v-else>{{ $t('notApplicable') }}</span>
                  </template>

                  <DxColumn
                    data-field="Observaciones"
                    :visible="newFieldsVisibility.Observaciones"
                    :caption="
                      $t('samplesRegister.sampleObservations')
                    "
                    :editor-options="{ maxLength: 2000 }"
                    alignment="center"
                  />
                  <DxColumn
                    data-field="ph"
                    :visible="newFieldsVisibility.inSituPh"
                    :caption="$t('samplesRegister.inSituFields.ph')"
                    alignment="center"
                  />
                  <DxColumn
                    data-field="temperatura"
                    :visible="newFieldsVisibility.inSituTemperatura"
                    :caption="
                      $t('samplesRegister.inSituFields.temperatura')
                    "
                    alignment="center"
                  />
                  <DxColumn
                    data-field="conductividadElectrica"
                    :visible="
                      newFieldsVisibility.inSituConductividadElectrica
                    "
                    :caption="
                      $t(
                        'samplesRegister.inSituFields.conductividadElectrica'
                      )
                    "
                    alignment="center"
                  />
                  <DxColumn
                    data-field="oxigenoDisuelto"
                    :visible="
                      newFieldsVisibility.inSituOxigenoDisuelto
                    "
                    :caption="
                      $t(
                        'samplesRegister.inSituFields.oxigenoDisuelto'
                      )
                    "
                    alignment="center"
                  />
                  <DxColumn
                    data-field="cloroLibre"
                    :visible="newFieldsVisibility.inSituCloroLibre"
                    :caption="
                      $t('samplesRegister.inSituFields.cloroLibre')
                    "
                    alignment="center"
                  />
                  <DxColumn
                    data-field="turbidez"
                    :visible="newFieldsVisibility.inSituTurbidez"
                    :caption="
                      $t('samplesRegister.inSituFields.turbidez')
                    "
                    alignment="center"
                  />
                  <DxColumn
                    data-field="monocloraminas"
                    :visible="
                      newFieldsVisibility.inSituMonocloraminas
                    "
                    :caption="
                      $t(
                        'samplesRegister.inSituFields.monocloraminas'
                      )
                    "
                    alignment="center"
                  />
                  <DxColumn
                    data-field="salinidad"
                    :visible="newFieldsVisibility.inSituSalinidad"
                    :caption="
                      $t('samplesRegister.inSituFields.salinidad')
                    "
                    alignment="center"
                  />
                  <DxColumn
                    data-field="materialFlotante"
                    :visible="
                      newFieldsVisibility.inSituMaterialFlotante
                    "
                    :caption="
                      $t(
                        'samplesRegister.inSituFields.materialFlotante'
                      )
                    "
                    alignment="center"
                  />
                  <!-- <DxColumn
                  data-field="tph"
                  :visible="newFieldsVisibility.inSituTph"
                  :caption="$t('samplesRegister.inSituFields.tph')"
                  alignment="center"
                /> -->
                  <DxColumn
                    data-field="potencialRedox"
                    :visible="
                      newFieldsVisibility.inSituPotencialRedox
                    "
                    :caption="
                      $t(
                        'samplesRegister.inSituFields.potencialRedox'
                      )
                    "
                    alignment="center"
                  />
                  <DxColumn
                    :buttons="updatedEditButtons"
                    :fixed="true"
                    fixed-position="right"
                    type="buttons"
                    name="actionColumn"
                    alignment="center"
                  />
                </DxDataGrid>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  DxForm,
  DxItem as DxFormItem,
  DxLabel as DxFormLabel,
  DxGroupItem as DxFormGroupItem,
} from 'devextreme-vue/form';

import {
  DxDataGrid,
  DxColumn,
  DxEditing,
  DxLoadPanel,
} from 'devextreme-vue/data-grid';

import DxSelectBox from 'devextreme-vue/select-box';
import DxCheckBox from 'devextreme-vue/check-box';
import DxNumberBox from 'devextreme-vue/number-box';
import DxTextBox from 'devextreme-vue/text-box';
import { mapActions, mapState } from 'vuex';
import { NOTIFY_TIME_LONG } from '@/js/constants';
import EventBus from '@/services/event-bus';
import { emitSamplesRegisterGa4Event } from '@/services/samplesRegisterAnalytics';
import iconTrash from '@/static/icons/ic_trash_new.svg';
import iconSave from '@/static/icons/ic_save_new.svg';
import iconPencil from '@/static/icons/ic_pencil.svg';

export default {
  components: {
    DxForm,
    DxFormItem,
    DxFormLabel,
    DxFormGroupItem,
    DxDataGrid,
    DxColumn,
    DxEditing,
    DxLoadPanel,
    DxSelectBox,
    DxCheckBox,
    DxNumberBox,
    DxTextBox,
  },

  props: {
    store: { type: [Array, Object, String], default: () => [] },
    invoiceSamples: { type: Array, default: () => [] },
  },

  data() {
    const hasInitialInvoiceSamples =
      Array.isArray(this.invoiceSamples) &&
      this.invoiceSamples.length > 0;

    return {
      formData: {
        CodCliente: null,
        CodPresupuesto: null,
        Sampler: null,
        SamplingDate: null,
      },
      rows: [],
      pendingRows: {},
      pendingRegisteredRows: [],
      editingRegisteredRowId: null,
      editingRegisteredRowSnapshot: null,
      editingRegisteredRowContextBudgetCode: null,
      skipNextInvoiceSamplesSync: false,
      lastHydratedBudgetCode: null,
      budgetChangedAfterHydration: false,
      allSampleTypesMap: {},
      allSubSampleTypesMap: {},
      sampleTypesCacheByFamilySubFamily: {},
      analysisTypeCacheByFamilySubFamily: {},
      isHydratingRegisteredRows: hasInitialInvoiceSamples,
      suppressNativePreloader: hasInitialInvoiceSamples,

      expressMode: false,
      familiDisabled: true,
      analysisType: [],
      familiesFiltered: [],
      offersFamiliesFiltered: [],
      subFamiliesFiltered: [],
      filteredParcels: [],
      newFieldsVisibility: {
        lote: false,
        SubTipoMuestra: false,
        cloroResidual: false,
        cma: false,
        Legislacion: false,
        automaticSamplingPoint: false,
        farmPoint: false,
        plotPoint: false,
        samplingPointCoordinate: false,
        lmr: false,
        trazaCultivo: false,
        CultivoOrganico: false,
        Observaciones: false,
        inSituPh: false,
        inSituTemperatura: false,
        inSituConductividadElectrica: false,
        inSituOxigenoDisuelto: false,
        inSituCloroLibre: false,
        inSituTurbidez: false,
        inSituMonocloraminas: false,
        inSituSalinidad: false,
        inSituMaterialFlotante: false,
        // inSituTph: false,
        inSituPotencialRedox: false,
      },
      fincaDisabled: false,
      parcelaDisabled: false,
      hideSamplingPoints: true,
      idSubFamiliaTiposMuestras: null,
      saveTemplateButtonInstance: null,
    };
  },

  computed: {
    company() {
      return this.$store?.state?.contact?.company;
    },
    registeredRowsForTemplate() {
      return this.rows.filter((row) => this.isRowRegistered(row));
    },
    hasRegisteredSamples() {
      return this.registeredRowsForTemplate.length > 0;
    },
    expressGridRenderKey() {
      return `express-grid-${
        this.hasMultipleBudgetCodesInInvoice ? 'multi' : 'single'
      }`;
    },
    hasMultipleBudgetCodesInInvoice() {
      const budgetCodes = new Set(
        (Array.isArray(this.invoiceSamples)
          ? this.invoiceSamples
          : []
        )
          .map((sample) => sample?.CodPresupuesto)
          .filter(
            (budgetCode) =>
              budgetCode !== null &&
              budgetCode !== undefined &&
              String(budgetCode).trim() !== ''
          )
      );

      return budgetCodes.size > 1;
    },
    updatedEditButtons() {
      return [
        {
          icon: iconSave,
          hint: this.$t('SaveSample'),
          onClick: this.registerSingleRow,
          visible: (e) => !this.isRowRegistered(e?.row?.data),
          cssClass: 'grid-button-icon',
        },
        {
          icon: iconTrash,
          onClick: this.deleteAtc,
          visible: (e) => !this.isRowRegistered(e?.row?.data),
          cssClass: 'grid-button-icon',
        },
        {
          icon: iconPencil,
          hint: this.$t('alert.edit'),
          cssClass:
            'grid-button-icon samples-list-details-edit-sample-button',
          onClick: this.startRegisteredRowEdit,
          visible: (e) =>
            !this.hasActiveRegisteredRowEdit() &&
            this.isRowRegistered(e?.row?.data) &&
            !this.isRegisteredRowBeingEdited(e?.row?.data),
        },
        {
          icon: iconSave,
          hint: this.$t('SaveSample'),
          onClick: this.saveRegisteredRowEdit,
          visible: (e) =>
            this.isRegisteredRowBeingEdited(e?.row?.data),
          cssClass: 'grid-button-icon',
        },
        {
          icon: iconTrash,
          hint: this.$t('delete'),
          onClick: this.deleteRegisteredRow,
          visible: (e) =>
            this.isRowRegistered(e?.row?.data) &&
            !this.hasActiveRegisteredRowEdit(),
          cssClass: 'grid-button-icon',
        },
      ];
    },
    ...mapState('Offers', [
      'detallesPresupuestos',
      'analisisTipoDescrip',
      'presupuestos',
    ]),
    ...mapState('samplesRegisterModule', [
      'sampletypes',
      'families',
      'offerSelected',
      'legislations',
      'farmsWithParcels',
      'idInvoice',
      'invoiceCodeInfoPopUp',
      'expressRegisterCache',
      'expressRegisterBudgetCacheByCode',
    ]),
  },

  watch: {
    hasRegisteredSamples() {
      this.applySaveTemplateDisabledState();
    },
    invoiceSamples: {
      immediate: true,
      async handler(samples) {
        if (!Array.isArray(samples) || !samples.length) return;
        if (this.skipNextInvoiceSamplesSync) {
          this.skipNextInvoiceSamplesSync = false;
          return;
        }
        try {
          await this.syncRowsFromInvoiceSamples();
        } catch (error) {
          console.error(
            '[ExpressSampleRegister] syncRowsFromInvoiceSamples failed',
            error
          );
        }
      },
    },
    'formData.CodPresupuesto': async function (nuevoValor) {
      const hasRegisteredRows = this.rows.some((row) =>
        this.isRowRegistered(row)
      );
      const shouldPreserveRows =
        hasRegisteredRows && !this.isHydratingRegisteredRows;
      const editingRegisteredRow = this.editingRegisteredRowId
        ? this.getRowFromStore(this.editingRegisteredRowId)
        : null;
      const isEditingRegisteredRow = Boolean(editingRegisteredRow);

      if (!shouldPreserveRows) {
        this.rows = [];
        this.pendingRows = {};
        this.ensureOneRow();
      }

      if (nuevoValor) {
        const restoredFromCache =
          !this.hasMultipleBudgetCodesInInvoice &&
          this.restoreExpressRegisterCache(nuevoValor);

        if (!restoredFromCache) {
          await this.fetchFamilies(nuevoValor);
          this.persistBudgetCacheEntry(nuevoValor);
          if (!this.hasMultipleBudgetCodesInInvoice) {
            this.persistExpressRegisterCache(nuevoValor);
          }
        }

        if (isEditingRegisteredRow) {
          this.applyBudgetChangeToEditingRegisteredRow(
            editingRegisteredRow,
            nuevoValor
          );
        }

        this.syncRegisteredRowsDisplayForBudget(nuevoValor);
      } else {
        this.familiDisabled = true;
        this.analysisType = [];
        this.familiesFiltered = [];
        this.offersFamiliesFiltered = [];
        this.clearExpressRegisterCache();
      }

      this.budgetChangedAfterHydration = shouldPreserveRows
        ? String(nuevoValor || '') !==
          String(this.lastHydratedBudgetCode || '')
        : false;
      this.emitMultiForm();
    },
    'formData.SamplingDate': async function (date) {
      this.setSamplingDate(date);
      this.emitMultiForm();
    },
  },

  beforeMount() {
    if (this.isHydratingRegisteredRows) {
      document?.body?.classList?.add(
        'express-sample-register-hydrating'
      );
    }

    if (this.offerSelected) {
      try {
        if (!this.suppressNativePreloader) {
          this.$f7.preloader.show();
        }
        this.codOfferPreSelected();
      } catch (error) {
        this.$notifyDX(
          {
            message: this.$t(
              this.$helpers.getFilteredErrorMessage(
                JSON.parse(error.message).error
              )
            ),
            width: 550,
          },
          'error',
          NOTIFY_TIME_LONG
        );
      } finally {
        if (!this.suppressNativePreloader) {
          this.$f7.preloader.hide();
        }
      }
    }

    if (
      !Array.isArray(this.farmsWithParcels) ||
      !this.farmsWithParcels.length
    ) {
      this.fetchFarmsAndParcels();
    }
  },

  created() {
    EventBus.$on('samplesRegister:expressMode', this.onExpressMode);
    EventBus.$on(
      'samplesRegister:expressGridFieldsVisibility',
      this.onExpressGridFieldsVisibility
    );
    EventBus.$on(
      'samplesRegister:confirmPopupClosed',
      this.applyPendingRegisteredRows
    );
  },

  beforeDestroy() {
    document?.body?.classList?.remove(
      'express-sample-register-hydrating'
    );
    EventBus.$off('samplesRegister:expressMode', this.onExpressMode);
    EventBus.$off(
      'samplesRegister:expressGridFieldsVisibility',
      this.onExpressGridFieldsVisibility
    );
    EventBus.$off(
      'samplesRegister:confirmPopupClosed',
      this.applyPendingRegisteredRows
    );
  },

  async mounted() {
    const hasInvoiceSamples =
      Array.isArray(this.invoiceSamples) &&
      this.invoiceSamples.length > 0;

    if (!hasInvoiceSamples) {
      this.ensureOneRow();
      this.emitMultiForm();
    }
  },

  methods: {
    skipNextInvoiceSamplesHydrationOnce() {
      this.skipNextInvoiceSamplesSync = true;
    },
    getAutomaticSamplingPointFromSample(sample) {
      const value =
        sample.automaticSamplingPoint ??
        sample.AutomaticSamplingPoint ??
        sample.PuntoMuestreoAutomatico ??
        sample.MuestreoAutomatico ??
        sample.EsPuntoMuestreoAutomatico ??
        true;

      if (typeof value === 'boolean') return value;
      if (typeof value === 'number') return value === 1;

      if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        return ['true', '1', 'si', 'sí', 's', 'yes'].includes(
          normalized
        );
      }

      return true;
    },

    async ensureFarmsAndParcelsLoaded() {
      if (
        Array.isArray(this.farmsWithParcels) &&
        this.farmsWithParcels.length
      ) {
        return;
      }

      await this.fetchFarmsAndParcels();
    },

    hydrateRegisteredSamplingPointUi(row) {
      const showSamplingPoints = Number(row?.FamiliaTM) !== 20;

      if (!showSamplingPoints || !row?.automaticSamplingPoint) {
        this.updateRowUi(row, {
          showSamplingPoints,
          filteredParcels: [],
          filteredLevelPoints: [],
        });
        return;
      }

      const farms = Array.isArray(this.farmsWithParcels)
        ? this.farmsWithParcels
        : [];

      let farm = farms.find(
        (item) => String(item.idFinca) === String(row.Parcelas)
      );

      if (!farm && row.FincaLiteral) {
        farm = farms.find(
          (item) =>
            String(item.Finca || '')
              .trim()
              .toLowerCase() ===
            String(row.FincaLiteral || '')
              .trim()
              .toLowerCase()
        );
      }

      const parcels = Array.isArray(farm?.Parcelas)
        ? [...farm.Parcelas]
        : [];

      if (farm && !row.FincaLiteral) {
        this.updateRowFields(row, {
          FincaLiteral: farm.Finca,
          Parcelas: farm.idFinca,
        });
      }

      let parcel = parcels.find(
        (item) => String(item.idParcela) === String(row.Parcela)
      );

      if (!parcel && row.ParcelaLiteral) {
        parcel = parcels.find(
          (item) =>
            String(item.Parcela || '')
              .trim()
              .toLowerCase() ===
            String(row.ParcelaLiteral || '')
              .trim()
              .toLowerCase()
        );
      }

      let levelPoints = [];
      if (Array.isArray(parcel?.nivelPuntos)) {
        levelPoints = [...parcel.nivelPuntos];
      } else if (Array.isArray(parcel?.NivelPuntos)) {
        levelPoints = [...parcel.NivelPuntos];
      }

      if (parcel && !row.ParcelaLiteral) {
        this.updateRowFields(row, {
          ParcelaLiteral: parcel.Parcela,
          Parcela: parcel.idParcela,
        });
      }

      this.updateRowUi(row, {
        showSamplingPoints,
        filteredParcels: parcels,
        filteredLevelPoints: levelPoints,
      });
    },

    rowHasMeaningfulData(row) {
      if (!row) return false;

      const fieldsToCheck = [
        'DescripcionMuestra',
        'FamiliaTM',
        'Descripcion',
        'Articulo',
        'SubTipoMuestra',
        'lote',
        'Observaciones',
        'Parcelas',
        'FincaLiteral',
        'Parcela',
        'ParcelaLiteral',
        'idNivelPunto',
        'Legislacion',
        'cloroResidual',
      ];

      return (
        fieldsToCheck.some((field) => {
          const value = row[field];
          return !(
            value === null ||
            value === undefined ||
            value === ''
          );
        }) ||
        row.cma === true ||
        row.lmr === true ||
        row.trazaCultivo === true ||
        row.CultivoOrganico === true
      );
    },

    applyVisibilityFromRegisteredRows(rows = []) {
      const hasValue = (field) =>
        rows.some((row) => {
          const value = row?.[field];
          return (
            value !== null && value !== undefined && value !== ''
          );
        });

      const hasBooleanTrue = (field) =>
        rows.some((row) => row?.[field] === true);

      this.newFieldsVisibility = {
        ...this.newFieldsVisibility,

        lote: this.newFieldsVisibility.lote || hasValue('lote'),

        SubTipoMuestra:
          this.newFieldsVisibility.SubTipoMuestra ||
          hasValue('SubTipoMuestra'),

        cloroResidual:
          this.newFieldsVisibility.cloroResidual ||
          hasValue('cloroResidual'),

        cma:
          this.newFieldsVisibility.cma ||
          hasBooleanTrue('cma') ||
          rows.some((row) => row?.ui?.checkCma),

        Legislacion:
          this.newFieldsVisibility.Legislacion ||
          hasValue('Legislacion') ||
          rows.some((row) => row?.ui?.legislationVisible),

        // Respetamos la preferencia visual del usuario al reabrir
        // borradores/plantillas: este campo tiene un valor por defecto
        // funcional en las filas y no debe forzar su propia columna.
        automaticSamplingPoint:
          this.newFieldsVisibility.automaticSamplingPoint,

        farmPoint:
          this.newFieldsVisibility.farmPoint ||
          hasValue('Parcelas') ||
          hasValue('FincaLiteral'),

        plotPoint:
          this.newFieldsVisibility.plotPoint ||
          hasValue('Parcela') ||
          hasValue('ParcelaLiteral'),

        samplingPointCoordinate:
          this.newFieldsVisibility.samplingPointCoordinate ||
          hasValue('idNivelPunto') ||
          hasValue('NivelPuntoLiteral'),

        lmr:
          this.newFieldsVisibility.lmr ||
          hasBooleanTrue('lmr') ||
          rows.some((row) => row?.ui?.checkLmr),

        trazaCultivo:
          this.newFieldsVisibility.trazaCultivo ||
          hasBooleanTrue('trazaCultivo'),

        CultivoOrganico:
          this.newFieldsVisibility.CultivoOrganico ||
          hasBooleanTrue('CultivoOrganico'),

        Observaciones:
          this.newFieldsVisibility.Observaciones ||
          hasValue('Observaciones'),
      };
    },

    async syncRowsFromInvoiceSamples() {
      if (!Array.isArray(this.invoiceSamples)) return;

      const samples = this.invoiceSamples;
      if (!samples.length) return;

      const currentRegisteredRows = this.rows.filter((row) =>
        this.isRowRegistered(row)
      );
      const sameRegisteredSampleCount =
        currentRegisteredRows.length === samples.length;

      document?.body?.classList?.add(
        'express-sample-register-hydrating'
      );
      this.isHydratingRegisteredRows = true;
      this.suppressNativePreloader = true;

      try {
        const firstSample = samples[0];
        const lastSample = samples[samples.length - 1];

        const codPresupuesto =
          lastSample?.CodPresupuesto || this.formData.CodPresupuesto;

        if (
          codPresupuesto &&
          this.formData.CodPresupuesto !== codPresupuesto
        ) {
          this.formData.CodPresupuesto = codPresupuesto;
          const restoredFromBudgetCache =
            this.restoreBudgetCacheEntryToState(codPresupuesto);
          if (!restoredFromBudgetCache) {
            const restoredFromCache =
              this.restoreExpressRegisterCache(codPresupuesto);
            if (!restoredFromCache) {
              await this.fetchFamilies(codPresupuesto);
            }
          }
        } else if (codPresupuesto && !this.analysisType.length) {
          const restoredFromBudgetCache =
            this.restoreBudgetCacheEntryToState(codPresupuesto);
          if (!restoredFromBudgetCache) {
            const restoredFromCache =
              this.restoreExpressRegisterCache(codPresupuesto);
            if (!restoredFromCache) {
              await this.fetchFamilies(codPresupuesto);
            }
          }
        }

        if (codPresupuesto !== null && !this.analysisType.length) {
          this.persistExpressRegisterCache(codPresupuesto);
        }

        if (codPresupuesto) {
          this.persistBudgetCacheEntry(codPresupuesto);
        }

        if (
          sameRegisteredSampleCount &&
          this.lastHydratedBudgetCode === codPresupuesto &&
          this.rows.length
        ) {
          this.syncRegisteredRowsDisplayForBudget(codPresupuesto);
          this.applyVisibilityFromRegisteredRows(this.rows);
          this.emitMultiForm();
          this.$nextTick(() => {
            this.refreshGrid();
          });
          return;
        }

        this.lastHydratedBudgetCode = codPresupuesto || null;
        this.budgetChangedAfterHydration = false;

        if (
          firstSample.FechaMuestreo &&
          !this.formData.SamplingDate
        ) {
          this.formData.SamplingDate = firstSample.FechaMuestreo;
        }

        const pendingRegisteredRowIds = new Set(
          (this.pendingRegisteredRows || [])
            .map((item) => item?.id)
            .filter((id) => id !== null && id !== undefined)
            .map((id) => String(id))
        );

        const draftRows = this.rows.filter((row) => {
          const rowId = row?.id;
          const isPendingRegistered =
            rowId !== null &&
            rowId !== undefined &&
            pendingRegisteredRowIds.has(String(rowId));

          return (
            !isPendingRegistered &&
            !this.isRowRegistered(row) &&
            this.rowHasMeaningfulData(row)
          );
        });
        const restoredRows = samples.map((sample) => {
          const row =
            this.createRegisteredRowFromInvoiceSample(sample);
          const budgetEntry = this.getBudgetCacheEntry(
            row?.CodPresupuesto
          );
          const selectedAnalysis =
            this.findMatchingAnalysisForRow(
              row,
              this.getAnalysisTypeListForRow(row)
            );
          const selectedFamily = Array.isArray(
            budgetEntry?.familiesFiltered
          )
            ? budgetEntry.familiesFiltered.find(
                (item) =>
                  Number(item?.idFamiliaTM) ===
                  Number(row?.FamiliaTM)
              )
            : null;

          const budgetDisplayText =
            this.getAnalysisDisplayTextFromBudget(row, budgetEntry) ||
            null;
          if (budgetDisplayText) {
            row.DescripcionArticulo = budgetDisplayText;
          } else if (selectedAnalysis?.AtDescrip) {
            row.DescripcionArticulo = selectedAnalysis.AtDescrip;
          }
          row.analysisDisplayText =
            row.DescripcionArticulo || row.analysisDisplayText || null;
          if (selectedFamily?.FamiliaTM) {
            row.DescripcionFamilia = selectedFamily.FamiliaTM;
          }

          return row;
        });

        await this.ensureFarmsAndParcelsLoaded();

        this.rows = [...restoredRows, ...draftRows];

        const rowsFromActiveBudget = restoredRows.filter(
          (row) =>
            String(row?.CodPresupuesto || '') ===
            String(codPresupuesto || '')
        );

        for (const row of rowsFromActiveBudget) {
          await this.hydrateRegisteredRowUi(row);
        }

        this.applyVisibilityFromRegisteredRows(this.rows);

        this.emitMultiForm();

        this.$nextTick(() => {
          this.refreshGrid();
        });
      } finally {
        document?.body?.classList?.remove(
          'express-sample-register-hydrating'
        );
        this.suppressNativePreloader = false;
        this.isHydratingRegisteredRows = false;
      }
    },

    createRegisteredRowFromInvoiceSample(sample) {
      const row = this.createRow();

      return {
        ...row,

        id: `registered-${
          sample.IdWMuestra || sample.idWMuestra || sample.CodMuestra
        }`,

        CodMuestra: sample.CodMuestra || null,
        CodPresupuesto: sample.CodPresupuesto || null,
        IdWMuestra: sample.IdWMuestra || sample.idWMuestra || null,

        DescripcionMuestra:
          sample.DescripcionMuestra || sample.Muestra || '',

        FamiliaTM:
          sample.idFamilia ||
          sample.IdFamilia ||
          sample.idFamiliaTM ||
          sample.FamiliaTM ||
          null,

        Descripcion:
          sample.idTipoMuestra ||
          sample.IdTipoMuestra ||
          sample.Descripcion ||
          null,

        DescripcionTipoMuestra:
          sample.DescripcionTipoMuestra ||
          sample.TipoMuestra ||
          sample.TipoDeMuestra ||
          sample.DescripcionTM ||
          sample.Descripcion ||
          null,

        idTipoMuestra:
          sample.idTipoMuestra || sample.IdTipoMuestra || null,

        SubTipoMuestra:
          sample.idSubTipoMuestra ||
          sample.IdSubTipoMuestra ||
          sample.SubTipoMuestra ||
          null,
        DescripcionSubTipoMuestra:
          sample.DescripcionSubTipoMuestra ||
          sample.SubTipoMuestraDescripcion ||
          sample.DescripcionSubTipo ||
          sample.DescripcionSTM ||
          (typeof sample.SubTipoMuestra === 'string'
            ? sample.SubTipoMuestra
            : null),

        Articulo: sample.CodArticulo || sample.Articulo || null,
        IdAnalisisTipo:
          sample.IdAnalisisTipo || sample.idAnalisisTipo || null,
        idPresupuestoATNoS:
          sample.idPresupuestoATNoS ||
          sample.IdPresupuestoATNoS ||
          null,

        DescripcionArticulo:
          sample.AtDescrip ||
          sample.DescripcionArticulo ||
          sample.DescripcionAT ||
          sample.Analisis ||
          sample.AnalisisTipo ||
          null,
        analysisDisplayText:
          sample.AtDescrip ||
          sample.DescripcionArticulo ||
          sample.DescripcionAT ||
          sample.Analisis ||
          sample.AnalisisTipo ||
          null,

        CodLinea: sample.CodLinea || null,
        idSubFamiliaTM:
          sample.idSubFamiliaTM ||
          sample.IdSubFamiliaTM ||
          sample.idSubFamilia ||
          sample.IdSubFamilia ||
          null,
        PrecioAT: sample.PrecioAT || null,
        Descuento: sample.Descuento || null,

        lote: sample.LoteCodigo || sample.Lote || null,

        Observaciones:
          sample.ObservacionesMuestra || sample.Observaciones || null,

        automaticSamplingPoint:
          this.getAutomaticSamplingPointFromSample(sample),

        Parcelas:
          sample.idFinca || sample.IdFinca || sample.Parcelas || null,

        FincaLiteral:
          sample.FincaLiteral ||
          sample.Finca ||
          sample.ZonaFinca ||
          sample.Zona ||
          null,

        Parcela:
          sample.idParcela ||
          sample.IdParcela ||
          sample.Parcela ||
          null,

        ParcelaLiteral:
          sample.ParcelaLiteral ||
          sample.NombreParcela ||
          sample.LugarParcela ||
          sample.Lugar ||
          null,

        idNivelPunto:
          sample.idNivelPunto || sample.IdNivelPunto || null,

        NivelPuntoLiteral:
          sample.NivelPuntoLiteral ||
          sample.NivelPunto ||
          sample.PuntoCoordenada ||
          sample.PuntoCoordenadaLiteral ||
          sample.Coordenada ||
          null,

        Legislacion:
          sample.idLegislacion ||
          sample.IdLegislacion ||
          sample.Legislacion ||
          null,

        cloroResidual:
          sample.CloroResidualLibre || sample.cloroResidual || null,

        cma: Boolean(sample.CMA ?? sample.cma),
        lmr: Boolean(sample.LMR ?? sample.lmr),
        trazaCultivo: Boolean(sample.Traza ?? sample.trazaCultivo),
        CultivoOrganico: Boolean(
          sample.CultivoOrganico ?? sample.cultivoOrganico
        ),

        ui: {
          ...row.ui,
          isRegistered: true,
          sampleTypesDisabled: true,
          analysisTypeDisabled: true,
        },
      };
    },

    async hydrateRegisteredRowUi(row) {
      if (!row?.FamiliaTM) return;

      await this.fetchSubFamiliesFiltered_forRow(row.FamiliaTM, row);

      const sampleTypes = this.getRowSampleTypes(row) || [];
      const selectedSampleType = sampleTypes.find(
        (item) =>
          String(item?.idTipoMuestra) ===
          String(row?.idTipoMuestra ?? row?.Descripcion)
      );

      const subSampleType = Array.isArray(
        selectedSampleType?.SubTiposMuestras
      )
        ? [...selectedSampleType.SubTiposMuestras]
        : [];
      const familyDescription =
        (this.families || []).find(
          (entry) =>
            Number(entry?.idFamiliaTM) === Number(row?.FamiliaTM)
        )?.FamiliaTM || null;

      this.updateRowFields(row, {
        DescripcionFamilia:
          familyDescription || row?.DescripcionFamilia || null,
        DescripcionTipoMuestra:
          selectedSampleType?.Descripcion ||
          row?.DescripcionTipoMuestra ||
          null,
        analysisDisplayText:
          row?.analysisDisplayText ||
          row?.DescripcionArticulo ||
          null,
      });

      this.updateRowUi(row, {
        sampleTypesDisabled: false,
        subSampleType,
        subSampleTypeVisible: subSampleType.length === 0,
        idSubFamiliaTiposMuestras: selectedSampleType
          ? Number(
              selectedSampleType.idSubFamiliaTM ??
                selectedSampleType.id_sub_familia
            )
          : row?.ui?.idSubFamiliaTiposMuestras ?? null,
        analysisTypeDisabled: false,
        chlorineDisabled: selectedSampleType?.idTipoMuestra === 77,
        isRegistered: true,
      });

      if (selectedSampleType) {
        await this.analysisTypeFiltered(
          row.FamiliaTM,
          Number(
            selectedSampleType.idSubFamiliaTM ??
              selectedSampleType.id_sub_familia
          )
        );
      }

      this.hydrateRegisteredSamplingPointUi(row);
    },

    onToolbarPreparing(e) {
      const toolbarItems = e.toolbarOptions.items;
      e.toolbarOptions.elementAttr = {
        class: 'custom-simple-toolbar',
      };

      toolbarItems.push({
        name: 'addRow',
        location: 'before',
        widget: 'dxButton',
        locateInMenu: 'never',
        options: {
          text: this.$t('samplesRegister.addRow'),
          icon: 'add',
          width: 220,
          elementAttr: {
            class: 'btn-add',
            'data-tour-id': 'samples-register-express-add-row-button',
          },
          onClick: () => {
            this.addRow();
          },
        },
      });

      toolbarItems.push({
        name: 'clearRows',
        location: 'before',
        widget: 'dxButton',
        locateInMenu: 'never',
        options: {
          text: this.$t('deleteAll'),
          icon: 'trash',
          width: 220,
          elementAttr: { class: 'btn-delete-all' },
          onClick: () => {
            this.clearAllRows();
          },
        },
      });

      toolbarItems.push({
        name: 'saveSamples',
        location: 'before',
        widget: 'dxButton',
        locateInMenu: 'never',
        options: {
          text: this.$t('SaveSamples'),
          icon: 'save',
          width: 220,
          elementAttr: {
            class: 'btn-save-samples',
            'data-tour-id':
              'samples-register-express-save-all-button',
          },
          onClick: () => {
            this.triggerSaveSamples();
          },
        },
      });

      toolbarItems.push({
        name: 'SaveTemplate',
        location: 'before',
        widget: 'dxButton',
        locateInMenu: 'never',
        options: {
          text: this.$t('saveTemplate'),
          icon: 'bookmark',
          width: 220,
          elementAttr: {
            class: 'btn-bulk-upload',
            'data-tour-id':
              'samples-register-express-save-template-button',
          },
          disabled: !this.hasRegisteredSamples,
          onInitialized: (args) => {
            this.saveTemplateButtonInstance = args.component;
            this.applySaveTemplateDisabledState();
          },
          onClick: () => {
            this.$emit('request-save-template');
          },
        },
      });

      // toolbarItems.push({
      //   name: 'LoadTemplate',
      //   location: 'before',
      //   widget: 'dxButton',
      //   locateInMenu: 'never',
      //   options: {
      //     text: this.$t('loadTemplate'),
      //     icon: 'folder',
      //     width: 205,
      //     elementAttr: { class: 'btn-load-template' },
      //     onClick: () => {
      //       this.openLoadTemplatePopup();
      //     },
      //   },
      // });
    },
    applySaveTemplateDisabledState() {
      this.saveTemplateButtonInstance?.option(
        'disabled',
        !this.hasRegisteredSamples
      );
    },
    triggerSaveSamples() {
      this.$emit('save-samples');
    },
    discardActiveRegisteredRowEdit() {
      if (
        !this.editingRegisteredRowId ||
        !this.editingRegisteredRowSnapshot
      ) {
        return;
      }

      const row = this.getRowFromStore(this.editingRegisteredRowId);
      if (row) {
        const snapshot = this.cloneRowSnapshot(
          this.editingRegisteredRowSnapshot
        );
        Object.keys(row).forEach((key) => {
          if (!(key in snapshot)) {
            this.$delete(row, key);
          }
        });
        Object.entries(snapshot).forEach(([key, value]) => {
          this.$set(row, key, value);
        });
      }

      this.editingRegisteredRowId = null;
      this.editingRegisteredRowSnapshot = null;
      this.restoreBudgetContextAfterRegisteredRowEdit();
      this.emitMultiForm();
      this.$nextTick(() => this.refreshGrid());
    },
    async goToTemplateCreatePage({ forceForTour = false } = {}) {
      try {
        await this.commitGridEdits();
        if (!forceForTour && !this.registeredRowsForTemplate.length) {
          return false;
        }

        const templateCreationDraft =
          this.buildTemplateCreationDraft();

        console.info(
          '[SimpleSampleRegister] Template creation draft prepared',
          {
            hasMultipleBudgets:
              this.hasMultipleBudgetCodesForTemplate(),
            registeredRows: this.registeredRowsForTemplate.length,
            draftBudgetDefault:
              templateCreationDraft?.defaults?.codPresupuesto || null,
            draftSampleBudgets: (templateCreationDraft?.samples || []).map(
              (sample) => sample?.codPresupuesto || null
            ),
            draft: templateCreationDraft,
          }
        );

        this.setTemplateCreationDraft(templateCreationDraft);

        if (!forceForTour) {
          emitSamplesRegisterGa4Event(
            this,
            'sampling_template_create_open',
            {
              screen_name: 'TemplateCreatePage',
              entry_point:
                'express_sample_register_save_template_button',
              template_mode: 'create',
              samples_count: this.registeredRowsForTemplate.length,
              budget_code: this.formData?.CodPresupuesto || undefined,
            }
          );
        }

        this.$f7router.navigate(
          {
            name: 'TemplateCreatePage',
          },
          {
            animate: true,
            transition: 'f7-fade',
          }
        );
        return true;
      } catch (error) {
        console.error(
          '[SimpleSampleRegister] Error opening template create page',
          {
            error,
            message: error?.message || null,
            stack: error?.stack || null,
            hasMultipleBudgets:
              this.hasMultipleBudgetCodesForTemplate?.() || false,
            registeredRows: this.registeredRowsForTemplate?.length || 0,
            registeredRowBudgetCodes: this.registeredRowsForTemplate.map(
              (row) =>
                this.getResolvedRowData(row)?.CodPresupuesto || null
            ),
          }
        );
        this.$notifyDX(
          {
            message: this.$t(
              this.$helpers.getFilteredErrorMessage(
                error?.response || error?.message || error
              )
            ),
            width: 550,
          },
          'error',
          NOTIFY_TIME_LONG
        );
        return false;
      }
    },
    setTourSaveTemplateButtonDisabled(isDisabled = false) {
      this.saveTemplateButtonInstance?.option(
        'disabled',
        Boolean(isDisabled)
      );
    },
    openLoadTemplatePopup() {
      this.$f7router.navigate(
        {
          name: 'TemplatesListPage',
        },
        {
          animate: true,
          transition: 'f7-fade',
        }
      );
    },
    deleteAtc(e) {
      try {
        this.$f7.preloader.show();
        const dataGrid = e.component;
        dataGrid.deleteRow(e.row.rowIndex);
      } catch (error) {
        this.$notifyDX(
          {
            message: this.$t(
              this.$helpers.getFilteredErrorMessage(error.message)
            ),
            width: 550,
          },
          'error',
          NOTIFY_TIME_LONG
        );
      } finally {
        this.$f7.preloader.hide();
      }
    },
    isRowLocked(rowData) {
      return (
        this.isRowRegistered(rowData) &&
        !this.isRegisteredRowBeingEdited(rowData)
      );
    },
    cloneRowSnapshot(row) {
      try {
        return JSON.parse(JSON.stringify(row || null));
      } catch (error) {
        return row ? { ...row } : null;
      }
    },
    isRegisteredRowBeingEdited(rowData) {
      const row = this.getResolvedRowData(rowData);
      return Boolean(
        row?.id &&
          this.editingRegisteredRowId &&
          String(row.id) === String(this.editingRegisteredRowId)
      );
    },
    hasActiveRegisteredRowEdit() {
      return Boolean(this.editingRegisteredRowId);
    },
    async startRegisteredRowEdit(e) {
      e?.event?.preventDefault?.();
      e?.event?.stopPropagation?.();
      const row = this.getResolvedRowData(e?.row?.data);
      if (!row || !this.isRowRegistered(row)) return;

      this.editingRegisteredRowContextBudgetCode =
        this.formData.CodPresupuesto || null;
      await this.prepareRegisteredRowEdit(row);
      this.editingRegisteredRowId = row.id;
      this.editingRegisteredRowSnapshot = this.cloneRowSnapshot(row);
      this.$nextTick(() => this.refreshGrid());
    },
    buildRegisteredRowUpdatePayload(rowData) {
      const row = this.getResolvedRowData(rowData);
      const analysisData = this.findMatchingAnalysisForRow(
        row,
        this.getAnalysisTypeListForRow(row)
      );

      return {
        ...row,
        CodCliente: this.getClientCode(),
        idAlbaran: this.idInvoice,
        CodAlbaran: this.invoiceCodeInfoPopUp,
        FechaMuestreo: this.formData.SamplingDate,
        SampleDescription: row?.DescripcionMuestra,
        CodArticulo: row?.Articulo,
        idFamiliaTM: row?.FamiliaTM,
        IdTipoMuestra: row?.idTipoMuestra ?? row?.Descripcion ?? null,
        IdSubTipoMuestra: row?.SubTipoMuestra ?? null,
        IdAnalisisTipo: analysisData?.IdAnalisisTipo ?? null,
        idPresupuestoATNoS:
          analysisData?.idPresupuestoATNoS ??
          analysisData?.IdPresupuestoATNoS ??
          null,
        idFinca: row?.automaticSamplingPoint ? row?.Parcelas : null,
        IdFinca: row?.automaticSamplingPoint ? row?.Parcelas : null,
        idParcela: row?.automaticSamplingPoint ? row?.Parcela : null,
        IdParcela: row?.automaticSamplingPoint ? row?.Parcela : null,
      };
    },
    hasRegisteredRowFamilyChanged(rowData) {
      const row = this.getResolvedRowData(rowData);
      const originalFamilyId =
        this.editingRegisteredRowSnapshot?.FamiliaTM ?? null;
      const nextFamilyId = row?.FamiliaTM ?? null;

      return (
        String(originalFamilyId ?? '') !== String(nextFamilyId ?? '')
      );
    },
    applyFramework7DialogTexts() {
      if (!this.$f7) return;

      this.$f7.params.dialog.buttonCancel = this.$t('cancel');
      this.$f7.params.dialog.buttonOk = this.$t('samplesRegister.ok');
    },
    async saveRegisteredRowEdit(e = null) {
      const row = e?.row?.data
        ? this.getResolvedRowData(e.row.data)
        : this.getRowFromStore(this.editingRegisteredRowId);
      if (!row || !this.isRegisteredRowBeingEdited(row)) return false;

      try {
        await this.commitGridEdits();
        if (!this.validateRowBeforeRegister(row)) {
          throw new Error('missing_express_fields');
        }

        this.$f7.preloader.show();
        if (this.hasRegisteredRowFamilyChanged(row)) {
          await this.deleteSample(row?.IdWMuestra);
          await this.registerRows([row]);
        } else {
          await this.updateSample(
            this.buildRegisteredRowUpdatePayload(row)
          );
        }
        this.editingRegisteredRowId = null;
        this.editingRegisteredRowSnapshot = null;
        this.restoreBudgetContextAfterRegisteredRowEdit();
        await this.fetchInvoiceSamples(this.idInvoice);
        return true;
      } catch (error) {
        const message =
          error?.message === 'missing_express_fields'
            ? this.$t('samplesRegister.missingFieldsError')
            : this.$t('internalServerError');
        this.$notifyDX(
          {
            message,
            width: 550,
          },
          'error',
          NOTIFY_TIME_LONG
        );
        return false;
      } finally {
        this.$f7.preloader.hide();
      }
    },
    async deleteRegisteredRow(e) {
      const row = this.getResolvedRowData(e?.row?.data);
      const sampleId = row?.IdWMuestra;
      if (!row || !sampleId) return;

      this.applyFramework7DialogTexts();
      this.$f7.dialog.confirm(
        `${this.$t('confirm_delete')} <b>${
          row?.CodMuestra || ''
        }</b>?`,
        async () => {
          try {
            this.$f7.preloader.show();
            await this.deleteSample(sampleId);
            await this.fetchInvoiceSamples(this.idInvoice);
          } catch (error) {
            this.$notifyDX(
              {
                message: this.$t('internalServerError'),
                width: 550,
              },
              'error',
              NOTIFY_TIME_LONG
            );
          } finally {
            this.$f7.preloader.hide();
          }
        }
      );
    },
    getRowFromStore(rowOrId) {
      const id = typeof rowOrId === 'string' ? rowOrId : rowOrId?.id;
      if (!id) return null;

      const rows = Array.isArray(this.rows) ? this.rows : [];
      const pendingRows = this.pendingRows || {};

      return (
        rows.find((r) => r && r.id === id) || pendingRows[id] || null
      );
    },

    getRowUiProp(cellData, prop) {
      const stored = this.getRowFromStore(cellData);
      if (stored?.ui) return stored.ui[prop];
      return cellData?.ui?.[prop];
    },

    updateRowUi(rowOrId, props) {
      const id = typeof rowOrId === 'string' ? rowOrId : rowOrId?.id;
      const target = typeof rowOrId === 'object' ? rowOrId : null;

      const idx = this.rows.findIndex((r) => r?.id === id);
      const rowAtIndex = idx !== -1 ? this.rows[idx] : null;
      if (rowAtIndex) {
        this.$set(this.rows[idx], 'ui', {
          ...(rowAtIndex.ui || {}),
          ...props,
        });
        if (target) {
          this.$set(target, 'ui', {
            ...(target.ui || {}),
            ...props,
          });
        }
        return;
      }

      if (this.pendingRows[id]) {
        this.$set(this.pendingRows[id], 'ui', {
          ...(this.pendingRows[id].ui || {}),
          ...props,
        });
        if (target) {
          this.$set(target, 'ui', {
            ...(target.ui || {}),
            ...props,
          });
        }
        return;
      }

      if (target) {
        this.$set(target, 'ui', {
          ...(target.ui || {}),
          ...props,
        });
      }
    },

    updateRowFields(rowOrId, fields) {
      const id = typeof rowOrId === 'string' ? rowOrId : rowOrId?.id;
      const target = typeof rowOrId === 'object' ? rowOrId : null;

      const idx = this.rows.findIndex((r) => r?.id === id);
      const rowAtIndex = idx !== -1 ? this.rows[idx] : null;
      if (rowAtIndex) {
        Object.entries(fields).forEach(([key, value]) =>
          this.$set(this.rows[idx], key, value)
        );
        if (target) {
          Object.entries(fields).forEach(([key, value]) =>
            this.$set(target, key, value)
          );
        }
        return;
      }

      if (this.pendingRows[id]) {
        Object.entries(fields).forEach(([key, value]) =>
          this.$set(this.pendingRows[id], key, value)
        );
        if (target) {
          Object.entries(fields).forEach(([key, value]) =>
            this.$set(target, key, value)
          );
        }
        return;
      }

      if (target) Object.assign(target, fields);
    },

    refreshRow(cell) {
      const grid = this.$refs.samplesGrid?.instance;
      if (!grid) return;
      let idx = null;
      if (typeof cell?.rowIndex === 'number') {
        idx = cell.rowIndex;
      } else if (typeof cell?.row?.rowIndex === 'number') {
        idx = cell.row.rowIndex;
      }
      if (idx != null && typeof grid.repaintRows === 'function') {
        grid.repaintRows([idx]);
      } else {
        grid.refresh();
      }
    },

    refreshGrid() {
      this.$refs.samplesGrid?.instance?.refresh();
    },

    createRegisteredRowBudgetChangeHandler(cell) {
      return (event) => {
        this.onRegisteredRowBudgetEditorChange(
          cell,
          event && event.value
        );
      };
    },

    onRegisteredRowBudgetEditorChange(cell, budgetCode) {
      const row = this.getResolvedRowData(cell?.data);
      if (!row) return;

      const nextBudgetCode = budgetCode || null;
      const currentBudgetCode = row?.CodPresupuesto || null;

      if (String(currentBudgetCode || '') === String(nextBudgetCode || '')) {
        return;
      }

      cell?.setValue?.(nextBudgetCode);
      if (this.isRegisteredRowBeingEdited(row)) {
        this.$set(row, 'CodPresupuesto', nextBudgetCode);
      } else {
        this.applyBudgetChangeToEditingRegisteredRow(
          row,
          nextBudgetCode
        );
      }
      this.formData.CodPresupuesto = nextBudgetCode;
    },

    onEditorPreparing(e) {
      if (e?.parentType !== 'dataRow') return;
      if (!e?.row?.data || !e?.dataField) return;
      if (!this.isRegisteredRowBeingEdited(e.row.data)) return;

      const defaultOnValueChanged = e.editorOptions?.onValueChanged;

      e.editorOptions = {
        ...e.editorOptions,
        onValueChanged: (args) => {
          if (typeof defaultOnValueChanged === 'function') {
            defaultOnValueChanged(args);
          }

          e.setValue?.(args?.value);

          const row = this.getResolvedRowData(e.row.data);
          if (row) {
            this.$set(row, e.dataField, args?.value);
          }
        },
      };
    },

    async commitGridEdits() {
      const grid = this.$refs.samplesGrid?.instance;
      if (!grid || typeof grid.saveEditData !== 'function') return;
      const result = grid.saveEditData();
      if (result && typeof result.then === 'function') {
        await result;
      }
    },

    getClientCode() {
      return (
        this.presupuestos?.[0]?.CodCliente ||
        this.company ||
        this.formData.CodCliente
      );
    },
    normalizeTemplateText(value) {
      if (value === null || value === undefined) return '';
      return String(value).trim();
    },

    getCurrentBudgetOption() {
      const budgetCode = this.formData.CodPresupuesto;
      if (!budgetCode) return null;

      const candidates = [
        ...(Array.isArray(this.store) ? this.store : []),
        ...(Array.isArray(this.presupuestos)
          ? this.presupuestos
          : []),
      ];

      return (
        candidates.find(
          (item) =>
            String(item?.CodPresupuesto) === String(budgetCode)
        ) || null
      );
    },

    getTemplateBudgetDefault() {
      if (!this.formData.CodPresupuesto) return null;

      const budget = this.getCurrentBudgetOption();
      return {
        id: this.formData.CodPresupuesto,
        descripcion:
          budget?.CodOfferTitle ||
          budget?.Titulo ||
          budget?.Descripcion ||
          budget?.descripcion ||
          String(this.formData.CodPresupuesto),
      };
    },

    getBudgetOptionByCode(budgetCode) {
      if (!budgetCode) return null;

      const candidates = [
        ...(Array.isArray(this.store) ? this.store : []),
        ...(Array.isArray(this.presupuestos)
          ? this.presupuestos
          : []),
      ];

      return (
        candidates.find(
          (item) =>
            String(item?.CodPresupuesto || '') ===
            String(budgetCode || '')
        ) || null
      );
    },
    getTemplateSampleBudgetValue(rowData) {
      const row = this.getResolvedRowData(rowData);
      const budgetCode =
        this.normalizeTemplateText(row?.CodPresupuesto) || null;
      if (!budgetCode) return null;

      const budget = this.getBudgetOptionByCode(budgetCode);
      return {
        id: budgetCode,
        descripcion:
          budget?.CodOfferTitle ||
          budget?.Titulo ||
          budget?.Descripcion ||
          budget?.descripcion ||
          String(budgetCode),
      };
    },
    getRegisteredTemplateBudgetCodes() {
      return [
        ...new Set(
          this.registeredRowsForTemplate
            .map((row) =>
              this.normalizeTemplateText(
                this.getResolvedRowData(row)?.CodPresupuesto
              )
            )
            .filter(Boolean)
        ),
      ];
    },
    hasMultipleBudgetCodesForTemplate() {
      return this.getRegisteredTemplateBudgetCodes().length > 1;
    },
    isExpressValueEmpty(value) {
      return (
        value === null ||
        value === undefined ||
        (typeof value === 'string' && value.trim() === '')
      );
    },

    isRowRegistered(rowData) {
      const row = this.getResolvedRowData(rowData);

      return Boolean(
        row?.ui?.isRegistered || row?.CodMuestra || row?.IdWMuestra
      );
    },

    isSingleSelectedFieldApplicable(row, fieldKey) {
      const ui = row?.ui || {};
      switch (fieldKey) {
        case 'cma':
          return Boolean(ui.checkCma);
        case 'Legislacion':
          return Boolean(ui.legislationVisible);
        case 'automaticSamplingPoint':
          return ui.showSamplingPoints !== false;
        case 'farmPoint':
        case 'plotPoint':
          return ui.showSamplingPoints !== false;
        case 'samplingPointCoordinate':
          return (
            ui.showSamplingPoints !== false &&
            row?.automaticSamplingPoint === true &&
            Array.isArray(ui.filteredLevelPoints) &&
            ui.filteredLevelPoints.length > 0
          );
        case 'lmr':
          return Boolean(ui.checkLmr);
        case 'trazaCultivo':
        case 'CultivoOrganico':
          return Boolean(ui.traceCultiveVisible);
        default:
          return true;
      }
    },

    isSingleSelectedFieldFilled(row, fieldKey) {
      const checkFields = [
        'cma',
        'automaticSamplingPoint',
        'lmr',
        'trazaCultivo',
        'CultivoOrganico',
      ];
      if (checkFields.includes(fieldKey)) {
        return typeof row?.[fieldKey] === 'boolean';
      }

      switch (fieldKey) {
        case 'farmPoint':
          if (row?.automaticSamplingPoint) {
            return !this.isExpressValueEmpty(row?.Parcelas);
          }
          return !this.isExpressValueEmpty(row?.FincaLiteral);
        case 'plotPoint':
          if (row?.automaticSamplingPoint) {
            return !this.isExpressValueEmpty(row?.Parcela);
          }
          return !this.isExpressValueEmpty(row?.ParcelaLiteral);
        case 'samplingPointCoordinate':
          return Number(row?.idNivelPunto) > 0;
        case 'Legislacion':
          return Number(row?.Legislacion) > 0;
        default:
          return !this.isExpressValueEmpty(row?.[fieldKey]);
      }
    },

    validateRowBeforeRegister(rowData) {
      const row = this.getResolvedRowData(rowData);
      const missingHeaderFields = [
        this.formData.CodPresupuesto,
        this.formData.SamplingDate,
        this.idInvoice,
      ].some((value) => this.isExpressValueEmpty(value));
      if (missingHeaderFields) return false;

      const requiredRowFields = [
        'DescripcionMuestra',
        'FamiliaTM',
        'Descripcion',
        'Articulo',
      ];
      const missingRequired = requiredRowFields.some((field) =>
        this.isExpressValueEmpty(row?.[field])
      );
      return !missingRequired;
    },

    async showExpressConfirmPopup(codes = []) {
      this.setSampleCodeInfoPopUp(codes);
      this.setConfirmExpressSamplePopUp(true);
      await this.$nextTick();
    },

    async queueRegisteredRowsForConfirmation(
      registrations = [],
      { showPopup = true } = {}
    ) {
      if (!registrations.length) return [];

      this.pendingRegisteredRows = registrations.map((item) => ({
        id: item.id,
        CodMuestra: item.CodMuestra,
        IdWMuestra: item.IdWMuestra,
      }));

      const codes = registrations
        .map((item) => item.CodMuestra)
        .filter(Boolean);
      if (showPopup) {
        await this.showExpressConfirmPopup(codes);
      }
      return codes;
    },

    applyPendingRegisteredRows() {
      if (!this.pendingRegisteredRows.length) return;

      this.pendingRegisteredRows.forEach((item) => {
        const row = this.getRowFromStore(item.id);
        if (!row) return;
        const budgetEntry = this.getBudgetCacheEntry(
          row?.CodPresupuesto
        );
        const matchingAnalysis = this.findMatchingAnalysisForRow(
          row,
          this.getAnalysisTypeListForRow(row)
        );
        const matchingFamily = Array.isArray(
          budgetEntry?.familiesFiltered
        )
          ? budgetEntry.familiesFiltered.find(
              (entry) =>
                Number(entry?.idFamiliaTM) === Number(row?.FamiliaTM)
            )
          : null;
        const matchingSampleType = Array.isArray(
          row?._ui?._sampletypes
        )
          ? row._ui._sampletypes.find(
              (entry) =>
                String(entry?.idTipoMuestra) ===
                String(row?.idTipoMuestra ?? row?.Descripcion)
            )
          : null;

        this.updateRowFields(row, {
          CodMuestra: item.CodMuestra,
          IdWMuestra: item.IdWMuestra,
          DescripcionFamilia:
            matchingFamily?.FamiliaTM ||
            row?.DescripcionFamilia ||
            null,
          DescripcionTipoMuestra:
            matchingSampleType?.Descripcion ||
            row?.DescripcionTipoMuestra ||
            null,
          DescripcionArticulo:
            matchingAnalysis?.AtDescrip ||
            row?.DescripcionArticulo ||
            null,
          analysisDisplayText:
            matchingAnalysis?.AtDescrip ||
            row?.analysisDisplayText ||
            row?.DescripcionArticulo ||
            null,
        });
        this.updateRowUi(row, {
          isRegistered: true,
        });
      });

      this.pendingRegisteredRows = [];
      this.emitMultiForm();
      this.$nextTick(() => this.refreshGrid());

      if (this.idInvoice) {
        this.skipNextInvoiceSamplesHydrationOnce();
        this.fetchInvoiceSamples(this.idInvoice);
      }
    },

    getRegisteredCodes() {
      return this.rows.map((row) => row?.CodMuestra).filter(Boolean);
    },

    async registerRows(rowsToRegister = []) {
      if (!rowsToRegister.length) return [];

      const normalizedRowsToRegister =
        this.normalizeRowsForRegister(rowsToRegister);

      // El payload del modo express usa nombres nuevos en frontend
      // ("isExpressMode" y "MuestrasExpress"), pero el store traduce
      // internamente ese lote a altas unitarias sobre la misma API.
      const responses = await this.registerInvoiceSamples({
        isExpressMode: true,
        CodCliente: this.getClientCode(),
        idAlbaran: this.idInvoice,
        CodAlbaran: this.invoiceCodeInfoPopUp,
        CodPresupuesto: this.formData.CodPresupuesto,
        Sampler: this.formData.Sampler,
        SamplingDate: this.formData.SamplingDate,
        MuestrasExpress: normalizedRowsToRegister,
      });

      return (responses || []).map((response, index) => ({
        id: rowsToRegister[index]?.id,
        CodMuestra: response?.CodMuestra,
        IdWMuestra:
          response?.IdWMuestra ?? response?.idWMuestra ?? null,
      }));
    },

    async registerSingleRow(e) {
      try {
        await this.commitGridEdits();
        const row = this.getResolvedRowData(e?.row?.data);
        if (!row || this.isRowRegistered(row)) return;

        if (!this.validateRowBeforeRegister(row)) {
          throw new Error('missing_express_fields');
        }

        this.$f7.preloader.show();
        const registrations = await this.registerRows([row]);
        await this.queueRegisteredRowsForConfirmation(registrations, {
          showPopup: true,
        });
      } catch (error) {
        const message =
          error?.message === 'missing_express_fields'
            ? this.$t('samplesRegister.missingFieldsError')
            : this.$t('internalServerError');
        this.$notifyDX(
          {
            message,
            width: 550,
          },
          'error',
          NOTIFY_TIME_LONG
        );
      } finally {
        this.$f7.preloader.hide();
      }
    },

    async registerPendingRows() {
      await this.commitGridEdits();
      const rowsToRegister = this.rows.filter(
        (row) => !this.isRowRegistered(row)
      );
      if (!rowsToRegister.length) return [];

      const registrations = await this.registerRows(rowsToRegister);
      return await this.queueRegisteredRowsForConfirmation(
        registrations,
        {
          showPopup: false,
        }
      );
    },

    onRowPrepared(e) {
      if (e?.rowType !== 'data') return;

      const isRegistered = this.isRowRegistered(e?.data);
      const isEditingRegistered =
        this.isRegisteredRowBeingEdited(e?.data);
      const rowElement =
        e.rowElement?.classList != null
          ? e.rowElement
          : e.rowElement?.get?.(0);
      if (!rowElement) return;

      rowElement.classList?.toggle(
        'express-sample-row--registered',
        isRegistered
      );
      rowElement.classList?.toggle(
        'express-sample-row--registered-editing',
        isEditingRegistered
      );

      const cells = rowElement.querySelectorAll
        ? rowElement.querySelectorAll('td')
        : [];
      cells.forEach((cell) => {
        if (isEditingRegistered) {
          cell.style.setProperty(
            'background-color',
            '#dceaf8',
            'important'
          );
        } else if (isRegistered) {
          cell.style.setProperty(
            'background-color',
            '#e6f6ea',
            'important'
          );
        } else {
          cell.style.removeProperty('background-color');
        }
      });
    },

    onEditingStart(e) {
      if (this.isRowLocked(e?.data)) {
        e.cancel = true;
      }
    },

    getFamilyText(rowOrId) {
      const row =
        rowOrId && typeof rowOrId === 'object'
          ? this.getResolvedRowData(rowOrId)
          : null;
      const id = row ? row?.FamiliaTM : rowOrId;
      const budgetEntry = row
        ? this.getBudgetCacheEntry(row?.CodPresupuesto)
        : null;

      if (
        row &&
        this.isRowRegistered(row) &&
        row?.DescripcionFamilia
      ) {
        return row.DescripcionFamilia;
      }

      if (id == null) return '';
      const familiesSource =
        budgetEntry?.familiesFiltered?.length &&
        row &&
        this.isRowRegistered(row)
          ? budgetEntry.familiesFiltered
          : this.familiesFiltered || [];
      const f = familiesSource.find(
        (x) => x.idFamiliaTM === id
      );
      return f ? f.FamiliaTM : String(id);
    },

    getSampleTypeText(rowData) {
      const stored = this.getRowFromStore(rowData) || rowData;

      if (stored?.DescripcionTipoMuestra) {
        return stored.DescripcionTipoMuestra;
      }

      const idTipo =
        stored?.idTipoMuestra ?? stored?.Descripcion;
      if (idTipo == null || idTipo === '') return '';

      const list = stored?.ui?.sampleTypes || [];
      const item = list.find(
        (x) => String(x.idTipoMuestra) === String(idTipo)
      );

      if (item) return item.Descripcion;

      return this.allSampleTypesMap[idTipo] || String(idTipo);
    },

    getSubTypeText(rowData) {
      const stored = this.getRowFromStore(rowData) || rowData;
      const id = stored?.SubTipoMuestra;
      if (id == null) return '';

      const list = stored?.ui?.subSampleType || [];
      const st = list.find((x) => x.idSubTipoMuestra === id);
      if (st) return st.Descripcion;

      if (stored?.DescripcionSubTipoMuestra) {
        return stored.DescripcionSubTipoMuestra;
      }

      return this.allSubSampleTypesMap[id] || String(id);
    },

    getAnalysisText(row) {
      const resolvedRow = this.getResolvedRowData(row);

      const cod = resolvedRow?.Articulo;
      if (!cod) return '';

      if (
        this.isRowRegistered(resolvedRow) &&
        (resolvedRow?.analysisDisplayText ||
          resolvedRow?.DescripcionArticulo)
      ) {
        const budgetEntry = this.getBudgetCacheEntry(
          resolvedRow?.CodPresupuesto
        );
        return (
          this.getAnalysisDisplayTextFromBudget(
            resolvedRow,
            budgetEntry
          ) ||
          resolvedRow.analysisDisplayText ||
          resolvedRow.DescripcionArticulo
        );
      }

      const budgetEntry = this.getBudgetCacheEntry(
        resolvedRow?.CodPresupuesto
      );

      const analysisTypeList = this.getAnalysisTypeListForRow(
        resolvedRow
      );
      const a = this.findMatchingAnalysisForRow(
        resolvedRow,
        analysisTypeList
      );

      if (a?.AtDescrip) return a.AtDescrip;
      if (a?.AnalisisTipo || a?.DescripcionAT) {
        return this.getAnalysisDisplayText({
          AnalisisTipo: a.AnalisisTipo || a.CodArticulo || resolvedRow?.Articulo || '',
          DescripcionAT:
            a.DescripcionAT ||
            a.CodAnalisisTipo ||
            a.DescripcionArticulo ||
            a.Descripcion ||
            a.Analisis ||
            '',
          IdFamilia: a.IdFamilia || a.idFamiliaTM || resolvedRow?.IdFamilia,
          IdSubFamilia:
            a.IdSubFamilia ??
            a.IdSubFamiliaTM ??
            a.idSubFamiliaTM ??
            resolvedRow?.IdSubFamilia ??
            resolvedRow?.IdSubFamiliaTM,
        });
      }

      if (resolvedRow?.DescripcionArticulo) {
        return resolvedRow.DescripcionArticulo;
      }

      return String(cod);
    },

    getRowSelectedAnalysis(rowData) {
      const resolvedRow = this.getResolvedRowData(rowData);
      const analysisTypeList = this.getAnalysisTypeListForRow(
        resolvedRow
      );
      return this.findMatchingAnalysisForRow(
        resolvedRow,
        analysisTypeList
      );
    },

    getAnalysisCacheKeyForRow(rowData) {
      const row = this.getResolvedRowData(rowData);
      const budgetCode = String(
        row?.CodPresupuesto || this.formData?.CodPresupuesto || ''
      );
      const familyId = this.normalizeSelectionId(
        row?.FamiliaTM ?? row?.IdFamiliaTM
      );
      const subFamilyId = this.normalizeSelectionId(
        row?.ui?.idSubFamiliaTiposMuestras ??
          row?.idSubFamiliaTM ??
          row?.IdSubFamiliaTM ??
          row?.IdSubFamilia
      );

      if (
        !budgetCode ||
        familyId === null ||
        subFamilyId === null
      ) {
        return null;
      }

      return `${budgetCode}::${familyId}::${subFamilyId}`;
    },

    findMatchingAnalysisForRow(rowData, analysisList = []) {
      const row = this.getResolvedRowData(rowData);
      const list = Array.isArray(analysisList) ? analysisList : [];
      const articleCode = row?.Articulo ?? row?.CodArticulo;
      const normalizedArticle =
        articleCode == null ? '' : String(articleCode).trim();

      if (normalizedArticle) {
        const matchByArticle = list.find((item) => {
          const itemArticle = item?.CodArticulo ?? item?.Articulo;
          return (
            itemArticle != null &&
            String(itemArticle).trim() === normalizedArticle
          );
        });
        if (matchByArticle) return matchByArticle;
      }

      const analysisId = this.normalizeSelectionId(
        row?.IdAnalisisTipo ?? row?.idAnalisisTipo
      );
      if (analysisId !== null) {
        const matchByAnalysisId = list.find(
          (item) =>
            this.normalizeSelectionId(
              item?.IdAnalisisTipo ?? item?.idAnalisisTipo
            ) === analysisId
        );
        if (matchByAnalysisId) return matchByAnalysisId;
      }

      const budgetAnalysisId = this.normalizeSelectionId(
        row?.idPresupuestoATNoS ?? row?.IdPresupuestoATNoS
      );
      if (budgetAnalysisId !== null) {
        const matchByBudgetAnalysisId = list.find(
          (item) =>
            this.normalizeSelectionId(
              item?.idPresupuestoATNoS ?? item?.IdPresupuestoATNoS
            ) === budgetAnalysisId
        );
        if (matchByBudgetAnalysisId) return matchByBudgetAnalysisId;
      }

      return null;
    },

    getAnalysisTypeListForRow(rowData, budgetEntry = null) {
      const row = this.getResolvedRowData(rowData);
      const resolvedBudgetEntry =
        budgetEntry ||
        this.getBudgetCacheEntry(row?.CodPresupuesto);
      const rowCacheKey = this.getAnalysisCacheKeyForRow(row);
      const rowCachedAnalysis = rowCacheKey
        ? this.analysisTypeCacheByFamilySubFamily?.[rowCacheKey]
        : null;
      const candidates = [
        rowCachedAnalysis,
        resolvedBudgetEntry?.analysisType,
        this.analysisType,
      ];

      const matchingList = candidates.find((list) =>
        this.findMatchingAnalysisForRow(row, list)
      );
      if (matchingList) return matchingList;

      return (
        candidates.find(
          (list) => Array.isArray(list) && list.length
        ) || []
      );
    },

    isLmrAnalysisSelected(rowData) {
      const analysis = this.getRowSelectedAnalysis(rowData);
      return Boolean(analysis && analysis.TipoServicioLmr != null);
    },

    getLegislationText(id) {
      if (id == null) return '';
      const item = (this.legislations || []).find(
        (x) => x.idLegislacion === id
      );
      return item ? item.Legislacion : String(id);
    },

    getBooleanMark(value) {
      return value ? 'Si' : 'No';
    },

    getResolvedRowData(rowData) {
      return this.getRowFromStore(rowData) || rowData || {};
    },

    getAutomaticSamplingPointValue(rowData) {
      return Boolean(
        this.getResolvedRowData(rowData).automaticSamplingPoint
      );
    },

    getCmaValueForSubmission(rowData) {
      if (!this.newFieldsVisibility?.cma) return false;
      return Boolean(this.getResolvedRowData(rowData)?.cma);
    },

    normalizeRowsForRegister(rows = []) {
      return rows.map((row) => ({
        ...this.getResolvedRowData(row),
        cma: this.getCmaValueForSubmission(row),
      }));
    },

    mapRowToTemplateSample(rowData, index) {
      const row = this.getResolvedRowData(rowData);
      const descripcionMuestra =
        row?.DescripcionMuestra?.trim?.() ||
        row?.descripcionMuestra?.trim?.() ||
        '';
      const sampleTypeId = row?.idTipoMuestra ?? row?.Descripcion;
      const farmId = row?.automaticSamplingPoint
        ? row?.Parcelas
        : null;
      const plotId = row?.automaticSamplingPoint
        ? row?.Parcela
        : null;
      const levelPointId =
        row?.automaticSamplingPoint && Number(row?.idNivelPunto) > 0
          ? row.idNivelPunto
          : null;

      const mappedSample = {
        order: index + 1,
        codPresupuesto: this.getTemplateSampleBudgetValue(row),
        descripcionMuestra,
        observaciones: row?.Observaciones?.trim?.() || '',
        familiaTM: row?.FamiliaTM
          ? {
              id: row.FamiliaTM,
              descripcion: this.getFamilyText(row.FamiliaTM),
            }
          : null,
        tipoMuestra: sampleTypeId
          ? {
              id: sampleTypeId,
              descripcion: this.getSampleTypeText(row),
            }
          : null,
        subTipoMuestra: row?.SubTipoMuestra
          ? {
              id: row.SubTipoMuestra,
              descripcion: this.getSubTypeText(row),
            }
          : null,
        articulo: row?.Articulo
          ? {
              id: row.Articulo,
              descripcion: this.getAnalysisText(row),
            }
          : null,
        legislacion: row?.Legislacion
          ? {
              id: row.Legislacion,
              descripcion: this.getLegislationText(row.Legislacion),
            }
          : null,
        automaticSamplingPoint: Boolean(row?.automaticSamplingPoint),
        finca: farmId
          ? {
              id: farmId,
              descripcion: this.getFarmText(row),
            }
          : null,
        parcela: plotId
          ? {
              id: plotId,
              descripcion: this.getPlotText(row),
            }
          : null,
        nivelPunto: levelPointId
          ? {
              id: levelPointId,
              descripcion: this.getLevelPointDisplayText(row),
            }
          : null,
        fincaLiteral: row?.automaticSamplingPoint
          ? null
          : row?.FincaLiteral?.trim?.() || null,
        parcelaLiteral: row?.automaticSamplingPoint
          ? null
          : row?.ParcelaLiteral?.trim?.() || null,
        cma: this.getCmaValueForSubmission(row),
        lmr: Boolean(row?.lmr),
        trazaCultivo: Boolean(row?.trazaCultivo),
        cultivoOrganico: Boolean(row?.CultivoOrganico),
      };

      return mappedSample;
    },

    buildTemplateCreationDraft() {
      const hasMultipleBudgets =
        this.hasMultipleBudgetCodesForTemplate();
      const payload = {
        template: {
          name: '',
          shortDescription: '',
        },
        samples: this.registeredRowsForTemplate.map((row, index) =>
          this.mapRowToTemplateSample(row, index)
        ),
        sourceRouteName:
          this.$f7router?.currentRoute?.name || 'SamplesRegister',
        sourceRegisterMode: 'express',
        sourceRegisterTab: 0,
      };

      const budgetDefault =
        !hasMultipleBudgets && this.getTemplateBudgetDefault();
      if (budgetDefault) {
        payload.defaults = {
          codPresupuesto: budgetDefault,
        };
      }

      return payload;
    },

    isSamplingPointSectionVisible(rowData) {
      return Boolean(
        this.getRowUiProp(rowData, 'showSamplingPoints') ?? true
      );
    },

    getRowParcels(rowData) {
      return this.getRowUiProp(rowData, 'filteredParcels') || [];
    },

    getRowLevelPoints(rowData) {
      return this.getRowUiProp(rowData, 'filteredLevelPoints') || [];
    },

    hasSelectedAutomaticPlot(rowData) {
      return Boolean(this.getResolvedRowData(rowData)?.Parcela);
    },

    isLevelPointSelectionAvailable(rowData) {
      return (
        this.isSamplingPointSectionVisible(rowData) &&
        this.getAutomaticSamplingPointValue(rowData) &&
        this.hasSelectedAutomaticPlot(rowData) &&
        this.getRowLevelPoints(rowData).length > 0
      );
    },

    getLevelPointOptionText(item) {
      if (!item) return '';
      const label = item.NivelPunto || this.$t('point');
      return item.GPSZ ? `${label} - ${item.GPSZ}` : label;
    },

    getLevelPointDisplayText(rowData) {
      const resolvedRow = this.getResolvedRowData(rowData);

      if (!this.isSamplingPointSectionVisible(rowData)) {
        return this.$t('notApplicable');
      }

      if (!this.getAutomaticSamplingPointValue(rowData)) {
        return this.$t('notApplicable');
      }

      const literal =
        resolvedRow.NivelPuntoLiteral ||
        resolvedRow.PuntoCoordenada ||
        resolvedRow.PuntoCoordenadaLiteral ||
        resolvedRow.Coordenada ||
        '';

      if (!this.hasSelectedAutomaticPlot(rowData)) {
        return literal || '';
      }

      const levelPoints = this.getRowLevelPoints(rowData);

      const selectedPoint = levelPoints.find(
        (item) =>
          String(item.idNivelPunto) ===
          String(resolvedRow.idNivelPunto)
      );

      if (selectedPoint) {
        return this.getLevelPointOptionText(selectedPoint);
      }

      if (literal) return literal;

      if (!levelPoints.length) {
        return this.$t('notApplicable');
      }

      return '';
    },

    getLevelPointEmptyStateText(rowData) {
      if (!this.isSamplingPointSectionVisible(rowData)) {
        return this.$t('notApplicable');
      }
      if (!this.getAutomaticSamplingPointValue(rowData)) {
        return this.$t('notApplicable');
      }
      if (!this.hasSelectedAutomaticPlot(rowData)) {
        return '';
      }
      return this.$t('notApplicable');
    },

    getFarmText(rowData) {
      const resolvedRow = this.getResolvedRowData(rowData);
      if (!resolvedRow) return '';

      if (!resolvedRow.automaticSamplingPoint) {
        return resolvedRow.FincaLiteral || '';
      }

      const farm = (this.farmsWithParcels || []).find(
        (f) => String(f.idFinca) === String(resolvedRow.Parcelas)
      );

      return farm ? farm.Finca : resolvedRow.FincaLiteral || '';
    },

    getPlotText(rowData) {
      const resolvedRow = this.getResolvedRowData(rowData);
      if (!resolvedRow) return '';

      if (!resolvedRow.automaticSamplingPoint) {
        return resolvedRow.ParcelaLiteral || '';
      }

      const rowParcels = this.getRowParcels(resolvedRow) || [];
      const byRow = rowParcels.find(
        (p) => String(p.idParcela) === String(resolvedRow.Parcela)
      );

      if (byRow) return byRow.Parcela;

      for (const farm of this.farmsWithParcels || []) {
        const parcel = (farm.Parcelas || []).find(
          (p) => String(p.idParcela) === String(resolvedRow.Parcela)
        );
        if (parcel) return parcel.Parcela;
      }

      return resolvedRow.ParcelaLiteral || '';
    },

    getRowSampleTypes(cellData) {
      const stored = this.getRowFromStore(cellData);
      const list =
        stored?.ui?.sampleTypes ||
        cellData?.ui?.sampleTypes ||
        [];
      const safeList = Array.isArray(list) ? list : [];
      return [...safeList].sort((a, b) =>
        String(a.Descripcion || '').localeCompare(
          String(b.Descripcion || '')
        )
      );
    },

    normalizeSelectionId(value) {
      if (value === null || value === undefined || value === '') {
        return null;
      }
      const normalizedValue = Number(value);
      return Number.isNaN(normalizedValue)
        ? value
        : normalizedValue;
    },

    getRowArticlesBySubFamily(cellData) {
      const stored = this.getRowFromStore(cellData);
      const analysisTypeList = this.getAnalysisTypeListForRow(
        stored || cellData
      );
      const id =
        stored?.ui?.idSubFamiliaTiposMuestras ??
        cellData?.ui?.idSubFamiliaTiposMuestras ??
        stored?.idSubFamiliaTM ??
        cellData?.idSubFamiliaTM;
      if (id == null) return [];
      const idNum = Number(id);
      if (Number.isNaN(idNum)) return [];
      return analysisTypeList.filter((a) => {
        const sub =
          a.IdSubFamilia != null ? a.IdSubFamilia : a.IdSubFamiliaTM;
        return Number(sub) === idNum;
      });
    },

    createRow(seed = null) {
      const row = {
        id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
        CodMuestra: null,
        CodPresupuesto: this.formData.CodPresupuesto ?? null,
        IdWMuestra: null,
        automaticSamplingPoint: true,
        Parcelas: null,
        FincaLiteral: null,
        Parcela: null,
        ParcelaLiteral: null,
        idNivelPunto: null,
        FamiliaTM: null,
        Descripcion: null,
        idTipoMuestra: null,
        SubTipoMuestra: null,
        DescripcionSubTipoMuestra: null,
        Articulo: null,
        DescripcionMuestra: null,
        Observaciones: null,
        ph: null,
        temperatura: null,
        conductividadElectrica: null,
        oxigenoDisuelto: null,
        cloroLibre: null,
        turbidez: null,
        monocloraminas: null,
        salinidad: null,
        materialFlotante: null,
        tph: null,
        potencialRedox: null,
        lote: null,
        CodLinea: null,
        idSubFamiliaTM: null,
        DescripcionFamilia: null,
        DescripcionTipoMuestra: null,
        DescripcionArticulo: null,
        cma: null,
        lmr: null,
        Legislacion: null,
        cloroResidual: null,
        trazaCultivo: false,
        CultivoOrganico: false,
        PrecioAT: null,
        Descuento: null,
        ui: {
          isRegistered: false,
          sampleTypesDisabled: true,
          subSampleTypeVisible: true,
          analysisTypeDisabled: true,
          traceCultiveVisible: false,
          legislationVisible: false,
          chlorineDisabled: false,
          checkCma: false,
          cmaValue: false,
          checkLmr: false,
          lmrValue: false,
          subSampleType: [],
          idSubFamiliaTiposMuestras: null,
          filteredParcels: [],
          filteredLevelPoints: [],
          showSamplingPoints: true,
          sampleTypes: [],
        },
      };

      if (!seed) return row;

      const seededSubSampleType = Array.isArray(
        seed?.ui?.subSampleType
      )
        ? [...seed.ui.subSampleType]
        : [];
      const seededSampleTypes = Array.isArray(seed?.ui?.sampleTypes)
        ? [...seed.ui.sampleTypes]
        : [];
      const seededParcels = Array.isArray(seed?.ui?.filteredParcels)
        ? [...seed.ui.filteredParcels]
        : [];
      const seededLevelPoints = Array.isArray(
        seed?.ui?.filteredLevelPoints
      )
        ? [...seed.ui.filteredLevelPoints]
        : [];
      const hasSelectedSampleType = Boolean(
        seed.Descripcion ?? seed.idTipoMuestra
      );

      return {
        ...row,
        CodPresupuesto:
          seed.CodPresupuesto ?? this.formData.CodPresupuesto ?? null,
        FamiliaTM: seed.FamiliaTM ?? null,
        DescripcionMuestra: seed.DescripcionMuestra ?? null,
        Descripcion: seed.Descripcion ?? null,
        idTipoMuestra: seed.idTipoMuestra ?? seed.Descripcion ?? null,
        SubTipoMuestra: seed.SubTipoMuestra ?? null,
        DescripcionSubTipoMuestra:
          seed.DescripcionSubTipoMuestra ?? null,
        Articulo: seed.Articulo ?? null,
        CodLinea: seed.CodLinea ?? null,
        idSubFamiliaTM: seed.idSubFamiliaTM ?? null,
        DescripcionFamilia: seed.DescripcionFamilia ?? null,
        DescripcionTipoMuestra:
          seed.DescripcionTipoMuestra ?? null,
        DescripcionArticulo: seed.DescripcionArticulo ?? null,
        PrecioAT: seed.PrecioAT ?? null,
        Descuento: seed.Descuento ?? null,
        automaticSamplingPoint:
          seed.automaticSamplingPoint == null
            ? row.automaticSamplingPoint
            : seed.automaticSamplingPoint,
        lote: this.newFieldsVisibility.lote
          ? seed.lote ?? null
          : null,
        cma: this.newFieldsVisibility.cma
          ? Boolean(seed.cma)
          : row.cma,
        Legislacion: this.newFieldsVisibility.Legislacion
          ? seed.Legislacion ?? null
          : null,
        lmr: this.newFieldsVisibility.lmr
          ? Boolean(seed.lmr)
          : row.lmr,
        trazaCultivo: this.newFieldsVisibility.trazaCultivo
          ? Boolean(seed.trazaCultivo)
          : row.trazaCultivo,
        CultivoOrganico: this.newFieldsVisibility.CultivoOrganico
          ? Boolean(seed.CultivoOrganico)
          : row.CultivoOrganico,
        cloroResidual: this.newFieldsVisibility.cloroResidual
          ? seed.cloroResidual ?? null
          : null,
        Observaciones: this.newFieldsVisibility.Observaciones
          ? seed.Observaciones ?? null
          : null,
        Parcelas: this.newFieldsVisibility.farmPoint
          ? seed.Parcelas ?? null
          : null,
        FincaLiteral:
          this.newFieldsVisibility.farmPoint &&
          !seed.automaticSamplingPoint
            ? seed.FincaLiteral ?? null
            : null,
        Parcela: this.newFieldsVisibility.plotPoint
          ? seed.Parcela ?? null
          : null,
        ParcelaLiteral:
          this.newFieldsVisibility.plotPoint &&
          !seed.automaticSamplingPoint
            ? seed.ParcelaLiteral ?? null
            : null,
        idNivelPunto: this.newFieldsVisibility.samplingPointCoordinate
          ? seed.idNivelPunto ?? null
          : null,
        ui: {
          ...row.ui,
          sampleTypesDisabled: !seed.FamiliaTM,
          subSampleTypeVisible: !seededSubSampleType.length,
          analysisTypeDisabled: !hasSelectedSampleType,
          chlorineDisabled: Boolean(seed?.ui?.chlorineDisabled),
          traceCultiveVisible: Boolean(
            seed?.ui?.traceCultiveVisible
          ),
          legislationVisible: Boolean(seed?.ui?.legislationVisible),
          checkCma: Boolean(seed?.ui?.checkCma),
          cmaValue: this.newFieldsVisibility.cma
            ? Boolean(seed.cma)
            : false,
          checkLmr: Boolean(seed?.ui?.checkLmr),
          lmrValue: this.newFieldsVisibility.lmr
            ? Boolean(seed.lmr)
            : false,
          subSampleType: seededSubSampleType,
          idSubFamiliaTiposMuestras:
            seed?.ui?.idSubFamiliaTiposMuestras ??
            seed?.idSubFamiliaTM ??
            null,
          filteredParcels: this.newFieldsVisibility.farmPoint
            ? seededParcels
            : [],
          filteredLevelPoints: this.newFieldsVisibility
            .samplingPointCoordinate
            ? seededLevelPoints
            : [],
          showSamplingPoints: this.newFieldsVisibility
            .automaticSamplingPoint
            ? seed?.ui?.showSamplingPoints ??
              row.ui.showSamplingPoints
            : row.ui.showSamplingPoints,
          sampleTypes: seededSampleTypes,
          isRegistered: false,
        },
      };
    },

    getSeedRowForNextSample() {
      if (!this.rows.length) return null;
      const currentBudgetCode = String(
        this.formData.CodPresupuesto || ''
      );

      const draftRows = this.rows.filter(
        (row) =>
          !this.isRowRegistered(row) &&
          String(row?.CodPresupuesto || '') === currentBudgetCode
      );

      const sourceRow = [...draftRows].reverse().find((row) => {
        const resolvedRow = this.getResolvedRowData(row);
        const sampleTypes = resolvedRow?.ui?.sampleTypes || [];

        return (
          resolvedRow?.FamiliaTM &&
          Array.isArray(sampleTypes) &&
          sampleTypes.length > 0
        );
      });

      if (!sourceRow) {
        const registeredSourceRow = [...this.rows]
          .reverse()
          .find((row) => {
            const resolvedRow = this.getResolvedRowData(row);
            const sampleTypes = resolvedRow?.ui?.sampleTypes || [];

            return (
              String(resolvedRow?.CodPresupuesto || '') ===
                currentBudgetCode &&
              resolvedRow?.FamiliaTM &&
              Array.isArray(sampleTypes) &&
              sampleTypes.length > 0
            );
          });

        if (!registeredSourceRow) return null;

        return this.getResolvedRowData(registeredSourceRow);
      }

      return this.getResolvedRowData(sourceRow);
    },

    ensureOneRow() {
      if (!this.rows.length) this.rows = [this.createRow()];
    },

    addRow() {
      const seed = this.getSeedRowForNextSample();
      const shouldCreateBlankRow = this.budgetChangedAfterHydration;

      this.rows.push(this.createRow(shouldCreateBlankRow ? null : seed));
      if (shouldCreateBlankRow) {
        this.budgetChangedAfterHydration = false;
      }
      this.emitMultiForm();

      this.$nextTick(() => this.refreshGrid());
    },

    clearAllRows() {
      const grid = this.$refs.samplesGrid?.instance;
      if (grid) grid.cancelEditData();

      this.rows = [];
      this.pendingRows = {};
      this.pendingRegisteredRows = [];
      this.emitMultiForm();
      this.refreshGrid();
    },
    resetForModeSwitch() {
      this.clearAllRows();
      this.formData = {
        CodCliente: null,
        CodPresupuesto: null,
        Sampler: null,
        SamplingDate: null,
      };
      this.analysisType = [];
      this.familiesFiltered = [];
      this.offersFamiliesFiltered = [];
      this.subFamiliesFiltered = [];
      this.filteredParcels = [];
      this.idSubFamiliaTiposMuestras = null;
      this.familiDisabled = true;
      this.pendingRegisteredRows = [];
      this.skipNextInvoiceSamplesSync = false;
      this.clearExpressRegisterCache();
      this.ensureOneRow();
      this.emitMultiForm();
    },
    restoreExpressRegisterCache(codPresupuesto) {
      if (this.hasMultipleBudgetCodesInInvoice) return false;

      const cache = this.expressRegisterCache;
      const sameBudget =
        String(cache?.budgetCode || '') ===
        String(codPresupuesto || '');

      if (!sameBudget) return false;

      const cachedAnalysisType = Array.isArray(cache?.analysisType)
        ? cache.analysisType
        : [];
      const cachedFamiliesFiltered = Array.isArray(
        cache?.familiesFiltered
      )
        ? cache.familiesFiltered
        : [];
      const cachedOffersFamiliesFiltered = Array.isArray(
        cache?.offersFamiliesFiltered
      )
        ? cache.offersFamiliesFiltered
        : [];

      if (!cachedAnalysisType.length || !cachedFamiliesFiltered.length) {
        return false;
      }

      this.analysisType = [...cachedAnalysisType];
      this.familiesFiltered = [...cachedFamiliesFiltered];
      this.offersFamiliesFiltered = [...cachedOffersFamiliesFiltered];
      this.familiDisabled = false;
      return true;
    },
    persistExpressRegisterCache(codPresupuesto) {
      if (this.hasMultipleBudgetCodesInInvoice) return;
      if (!codPresupuesto || !Array.isArray(this.analysisType)) return;

      this.setExpressRegisterCache({
        budgetCode: codPresupuesto,
        analysisType: [...this.analysisType],
        familiesFiltered: Array.isArray(this.familiesFiltered)
          ? [...this.familiesFiltered]
          : [],
        offersFamiliesFiltered: Array.isArray(
          this.offersFamiliesFiltered
        )
          ? [...this.offersFamiliesFiltered]
          : [],
      });
    },
    persistBudgetCacheEntry(
      codPresupuesto,
      {
        analysisType = this.analysisType,
        familiesFiltered = this.familiesFiltered,
        offersFamiliesFiltered = this.offersFamiliesFiltered,
      } = {}
    ) {
      if (!codPresupuesto) return;

      this.setExpressRegisterBudgetCacheByCode({
        [codPresupuesto]: {
          analysisType: Array.isArray(analysisType)
            ? [...analysisType]
            : [],
          familiesFiltered: Array.isArray(familiesFiltered)
            ? [...familiesFiltered]
            : [],
          offersFamiliesFiltered: Array.isArray(offersFamiliesFiltered)
            ? [...offersFamiliesFiltered]
            : [],
        },
      });
    },
    restoreBudgetCacheEntryToState(codPresupuesto) {
      const budgetEntry = this.getBudgetCacheEntry(codPresupuesto);
      if (!budgetEntry) return false;

      const cachedAnalysisType = Array.isArray(budgetEntry.analysisType)
        ? budgetEntry.analysisType
        : [];
      const cachedFamiliesFiltered = Array.isArray(
        budgetEntry.familiesFiltered
      )
        ? budgetEntry.familiesFiltered
        : [];
      const cachedOffersFamiliesFiltered = Array.isArray(
        budgetEntry.offersFamiliesFiltered
      )
        ? budgetEntry.offersFamiliesFiltered
        : [];

      if (!cachedAnalysisType.length || !cachedFamiliesFiltered.length) {
        return false;
      }

      this.analysisType = [...cachedAnalysisType];
      this.familiesFiltered = [...cachedFamiliesFiltered];
      this.offersFamiliesFiltered = [...cachedOffersFamiliesFiltered];
      this.familiDisabled = false;
      return true;
    },
    syncRegisteredRowsDisplayForBudget(codPresupuesto) {
      if (!codPresupuesto) return;

      const budgetEntry = this.getBudgetCacheEntry(codPresupuesto);
      if (!budgetEntry) return;

      this.rows.forEach((row) => {
        if (
          !this.isRowRegistered(row) ||
          String(row?.CodPresupuesto || '') !==
            String(codPresupuesto || '')
        ) {
          return;
        }

        const matchingAnalysis = this.findMatchingAnalysisForRow(
          row,
          this.getAnalysisTypeListForRow(row)
        );

        const matchingFamily = Array.isArray(
          budgetEntry?.familiesFiltered
        )
          ? budgetEntry.familiesFiltered.find(
              (entry) =>
                Number(entry?.idFamiliaTM) === Number(row?.FamiliaTM)
            )
          : null;

        const matchingSampleType = Array.isArray(
          row?._ui?._sampletypes
        )
          ? row._ui._sampletypes.find(
              (entry) =>
                String(entry?.idTipoMuestra) ===
                String(row?.idTipoMuestra ?? row?.Descripcion)
            )
          : null;

        this.updateRowFields(row, {
          DescripcionFamilia:
            matchingFamily?.FamiliaTM ||
            row?.DescripcionFamilia ||
            null,
          DescripcionTipoMuestra:
            matchingSampleType?.Descripcion ||
            row?.DescripcionTipoMuestra ||
            null,
          DescripcionArticulo:
            matchingAnalysis?.AtDescrip ||
            row?.DescripcionArticulo ||
            null,
          analysisDisplayText:
            matchingAnalysis?.AtDescrip ||
            row?.analysisDisplayText ||
            row?.DescripcionArticulo ||
            null,
        });
      });
    },
    getBudgetCacheEntry(codPresupuesto) {
      if (!codPresupuesto) return null;
      return (
        this.expressRegisterBudgetCacheByCode?.[codPresupuesto] || null
      );
    },
    async ensureBudgetCacheEntry(codPresupuesto) {
      if (!codPresupuesto) return null;

      const cached = this.getBudgetCacheEntry(codPresupuesto);
      if (
        cached?.analysisType?.length &&
        cached?.familiesFiltered?.length
      ) {
        return cached;
      }

      const currentAnalysisType = Array.isArray(this.analysisType)
        ? [...this.analysisType]
        : [];
      const currentFamiliesFiltered = Array.isArray(
        this.familiesFiltered
      )
        ? [...this.familiesFiltered]
        : [];
      const currentOffersFamiliesFiltered = Array.isArray(
        this.offersFamiliesFiltered
      )
        ? [...this.offersFamiliesFiltered]
        : [];

      await this.fetchFamilies(codPresupuesto);

      const loadedEntry = {
        analysisType: Array.isArray(this.analysisType)
          ? [...this.analysisType]
          : [],
        familiesFiltered: Array.isArray(this.familiesFiltered)
          ? [...this.familiesFiltered]
          : [],
        offersFamiliesFiltered: Array.isArray(
          this.offersFamiliesFiltered
        )
          ? [...this.offersFamiliesFiltered]
          : [],
      };

      this.persistBudgetCacheEntry(codPresupuesto, loadedEntry);

      this.analysisType = currentAnalysisType;
      this.familiesFiltered = currentFamiliesFiltered;
      this.offersFamiliesFiltered = currentOffersFamiliesFiltered;

      return loadedEntry;
    },
    async prepareRegisteredRowEdit(rowData) {
      const row = this.getResolvedRowData(rowData);
      const rowBudgetCode = row?.CodPresupuesto;

      if (!row) return;

      if (rowBudgetCode) {
        const budgetEntry =
          (await this.ensureBudgetCacheEntry(rowBudgetCode)) ||
          this.getBudgetCacheEntry(rowBudgetCode);

        if (budgetEntry) {
          this.formData.CodPresupuesto = rowBudgetCode;
          this.analysisType = Array.isArray(budgetEntry.analysisType)
            ? [...budgetEntry.analysisType]
            : [];
          this.familiesFiltered = Array.isArray(
            budgetEntry.familiesFiltered
          )
            ? [...budgetEntry.familiesFiltered]
            : [];
          this.offersFamiliesFiltered = Array.isArray(
            budgetEntry.offersFamiliesFiltered
          )
            ? [...budgetEntry.offersFamiliesFiltered]
            : [];
          this.familiDisabled =
            this.familiesFiltered.length === 0;
        }
      }

      await this.hydrateRegisteredRowUi(row);
      if (rowBudgetCode) {
        this.syncRegisteredRowsDisplayForBudget(rowBudgetCode);
      }
    },
    applyBudgetChangeToEditingRegisteredRow(rowData, budgetCode) {
      const row = this.getResolvedRowData(rowData);
      if (!row) return;

      const currentBudgetCode = String(row?.CodPresupuesto || '');
      const nextBudgetCode = String(budgetCode || '');

      if (currentBudgetCode === nextBudgetCode) return;

      this.updateRowFields(row, {
        CodPresupuesto: budgetCode || null,
        FamiliaTM: null,
        Descripcion: null,
        idTipoMuestra: null,
        SubTipoMuestra: null,
        DescripcionSubTipoMuestra: null,
        Articulo: null,
        CodLinea: null,
        idSubFamiliaTM: null,
        DescripcionFamilia: null,
        DescripcionTipoMuestra: null,
        DescripcionArticulo: null,
        Legislacion: null,
        cma: false,
        lmr: false,
        trazaCultivo: false,
        CultivoOrganico: false,
        cloroResidual: null,
      });

      this.updateRowUi(row, {
        sampleTypesDisabled: true,
        subSampleType: [],
        subSampleTypeVisible: true,
        idSubFamiliaTiposMuestras: null,
        analysisTypeDisabled: true,
        traceCultiveVisible: false,
        legislationVisible: false,
        checkCma: false,
        cmaValue: false,
        checkLmr: false,
        lmrValue: false,
        chlorineDisabled: false,
        sampleTypes: [],
      });
    },
    restoreBudgetContextAfterRegisteredRowEdit() {
      const budgetCode =
        this.editingRegisteredRowContextBudgetCode ||
        this.lastHydratedBudgetCode ||
        null;

      this.editingRegisteredRowContextBudgetCode = null;

      if (budgetCode) {
        this.formData.CodPresupuesto = budgetCode;
      }
    },
    // bulkUpload() {
    //   console.log('cargando masivamente');
    // },
    onInitNewRow(e) {
      const newRow = this.createRow();
      Object.assign(e.data, newRow);
      this.$set(this.pendingRows, newRow.id, e.data);
    },

    onRowInserted(e) {
      const id = e.data?.id;
      if (!id) return;

      const pending = this.pendingRows[id];
      if (pending && pending !== e.data) {
        Object.assign(e.data, pending);
      }

      if (!this.rows.some((r) => r.id === id)) {
        this.rows.push(e.data);
      }
      this.$delete(this.pendingRows, id);
      this.emitMultiForm();
    },

    onRowRemoved(e) {
      const id = e.data?.id;
      if (!id) return;
      this.$delete(this.pendingRows, id);
      const idx = this.rows.findIndex((r) => r.id === id);
      if (idx !== -1) this.rows.splice(idx, 1);
      this.emitMultiForm();
    },

    emitMultiForm() {
      // Evento renombrado a ExpressFormData para reflejar el nuevo
      // naming del flujo y evitar mezclar el estado del formulario
      // express con el formulario estandar en SamplesRegister.
      EventBus.$emit('ExpressFormData', {
        ...this.formData,
        isExpressMode: true,
        MuestrasExpress: this.rows,
      });
    },

    async onRowFamilyChanged(IdFamilia, cell) {
      const row = cell.data;
      cell.setValue(IdFamilia);
      const selectedFamily = (this.familiesFiltered || []).find(
        (item) => String(item?.idFamiliaTM) === String(IdFamilia)
      );

      this.updateRowFields(row, {
        FamiliaTM: IdFamilia,
        DescripcionFamilia: selectedFamily?.FamiliaTM || null,
        Descripcion: null,
        idTipoMuestra: null,
        DescripcionTipoMuestra: null,
        SubTipoMuestra: null,
        DescripcionSubTipoMuestra: null,
        Articulo: null,
        DescripcionArticulo: null,
        idSubFamiliaTM: null,
        Legislacion: null,
        cma: false,
        lmr: false,
        trazaCultivo: false,
        CultivoOrganico: false,
        cloroResidual: null,
        Parcelas: null,
        Parcela: null,
        FincaLiteral: null,
        ParcelaLiteral: null,
        idNivelPunto: null,
      });
      this.updateRowUi(row, {
        sampleTypesDisabled: true,
        subSampleType: [],
        subSampleTypeVisible: true,
        idSubFamiliaTiposMuestras: null,
        analysisTypeDisabled: true,
        traceCultiveVisible: false,
        legislationVisible: false,
        checkCma: false,
        cmaValue: false,
        checkLmr: false,
        lmrValue: false,
        chlorineDisabled: false,
        filteredParcels: [],
        filteredLevelPoints: [],
        showSamplingPoints: Number(IdFamilia) !== 20,
        sampleTypes: [],
      });

      this.$nextTick(() => this.refreshRow(cell));

      if (IdFamilia) {
        await this.fetchSubFamiliesFiltered_forRow(IdFamilia, row);
      }

      this.$nextTick(() => this.refreshRow(cell));
      this.emitMultiForm();
    },

    async onRowSampleTypeChanged(idTipoMuestra, cell) {
      const row = cell.data;

      const stored = this.getRowFromStore(row);
      const list =
        stored?.ui?.sampleTypes || row?.ui?.sampleTypes || [];
      const selected = list.find(
        (x) => x.idTipoMuestra === idTipoMuestra
      );

      this.updateRowFields(row, {
        SubTipoMuestra: null,
        DescripcionSubTipoMuestra: null,
        Articulo: null,
        Legislacion: null,
        cma: false,
        lmr: false,
        trazaCultivo: false,
        CultivoOrganico: false,
        cloroResidual: null,
        idTipoMuestra,
      });

      if (selected) {
        if (Array.isArray(selected.SubTiposMuestras)) {
          selected.SubTiposMuestras.forEach((sub) => {
            this.$set(
              this.allSubSampleTypesMap,
              sub.idSubTipoMuestra,
              sub.Descripcion
            );
          });
        }

        const hasSubTypes =
          Array.isArray(selected.SubTiposMuestras) &&
          selected.SubTiposMuestras.length > 0;
        this.updateRowUi(row, {
          subSampleType: hasSubTypes ? selected.SubTiposMuestras : [],
          subSampleTypeVisible: !hasSubTypes,
          idSubFamiliaTiposMuestras: Number(
            selected.idSubFamiliaTM ?? selected.id_sub_familia
          ),
          analysisTypeDisabled: false,
          chlorineDisabled: selected.idTipoMuestra === 77,
          traceCultiveVisible: false,
          legislationVisible: false,
          checkCma: false,
          cmaValue: false,
          checkLmr: false,
          lmrValue: false,
        });
        await this.analysisTypeFiltered(
          row.FamiliaTM,
          Number(selected.idSubFamiliaTM ?? selected.id_sub_familia)
        );
      } else {
        this.updateRowUi(row, {
          subSampleType: [],
          subSampleTypeVisible: true,
          idSubFamiliaTiposMuestras: null,
          analysisTypeDisabled: true,
          chlorineDisabled: false,
          traceCultiveVisible: false,
          legislationVisible: false,
          checkCma: false,
          cmaValue: false,
          checkLmr: false,
          lmrValue: false,
        });
        this.analysisType = [];
      }

      cell.setValue(idTipoMuestra);
      this.emitMultiForm();
      this.$nextTick(() => this.refreshRow(cell));
    },

    onRowSubTypeChanged(value, cell) {
      const options =
        this.getRowUiProp(cell.data, 'subSampleType') || [];
      const selected = options.find(
        (item) => String(item?.idSubTipoMuestra) === String(value)
      );
      cell.setValue(value);
      this.updateRowFields(cell.data, {
        SubTipoMuestra: value,
        DescripcionSubTipoMuestra: selected?.Descripcion || null,
      });
      this.emitMultiForm();
    },

    onRowFieldChanged(field, value, cell) {
      this.updateRowFields(cell.data, { [field]: value });
      if (cell?.column?.dataField === field) cell.setValue(value);
      this.emitMultiForm();
      this.$nextTick(() => this.refreshRow(cell));
    },

    onRowAutomaticSamplingPointChanged(value, cell) {
      this.updateRowFields(cell.data, {
        automaticSamplingPoint: value,
        Parcelas: null,
        Parcela: null,
        FincaLiteral: null,
        ParcelaLiteral: null,
        idNivelPunto: null,
      });
      this.updateRowUi(cell.data, {
        filteredParcels: [],
        filteredLevelPoints: [],
      });
      cell.setValue(value);
      this.emitMultiForm();
      this.$nextTick(() => this.refreshGrid());
    },

    onRowFarmChanged(idFinca, cell) {
      const farm = (this.farmsWithParcels || []).find(
        (f) => f.idFinca === idFinca
      );
      this.updateRowFields(cell.data, {
        Parcelas: idFinca,
        FincaLiteral: farm ? farm.Finca : null,
        Parcela: null,
        ParcelaLiteral: null,
        idNivelPunto: null,
      });
      this.updateRowUi(cell.data, {
        filteredParcels: farm ? [...(farm.Parcelas || [])] : [],
        filteredLevelPoints: [],
      });
      cell.setValue(farm ? farm.Finca : null);
      this.emitMultiForm();
      this.$nextTick(() => this.refreshGrid());
    },

    onRowPlotChanged(idParcela, cell) {
      const parcel = this.getRowParcels(cell.data).find(
        (p) => p.idParcela === idParcela
      );
      const levelPoints = Array.isArray(parcel?.nivelPuntos)
        ? [...parcel.nivelPuntos]
        : [];
      this.updateRowFields(cell.data, {
        Parcela: idParcela,
        ParcelaLiteral: parcel ? parcel.Parcela : null,
        idNivelPunto: null,
      });
      this.updateRowUi(cell.data, {
        filteredLevelPoints: levelPoints,
      });
      cell.setValue(parcel ? parcel.Parcela : null);
      this.emitMultiForm();
      this.$nextTick(() => this.refreshGrid());
    },

    onRowLevelPointChanged(idNivelPunto, cell) {
      const selectedPoint = this.getRowLevelPoints(cell.data).find(
        (item) => item.idNivelPunto === idNivelPunto
      );

      this.updateRowFields(cell.data, {
        idNivelPunto,
      });
      cell.setValue(
        selectedPoint ? selectedPoint.idNivelPunto : null
      );
      this.emitMultiForm();
      this.$nextTick(() => this.refreshGrid());
    },

    async onRowAnalysisChanged(CodArticulo, cell) {
      const row = cell.data;
      const analysisTypeList = this.getAnalysisTypeListForRow(row);
      cell.setValue(CodArticulo);

      const cma = analysisTypeList.find(
        (item) =>
          item.TipoServicioCma != null &&
          String(item.CodArticulo) === String(CodArticulo)
      );
      const lmr = analysisTypeList.find(
        (item) =>
          item.TipoServicioLmr != null &&
          String(item.CodArticulo) === String(CodArticulo)
      );
      const articulo = analysisTypeList.find(
        (item) => String(item.CodArticulo) === String(CodArticulo)
      );

      if (articulo) {
        this.updateRowFields(row, {
          Articulo: CodArticulo,
          CodLinea: articulo.CodLinea,
          idSubFamiliaTM:
            articulo.IdSubFamilia ??
            articulo.IdSubFamiliaTM ??
            row?.idSubFamiliaTM ??
            null,
          IdAnalisisTipo: articulo.IdAnalisisTipo ?? null,
          idPresupuestoATNoS:
            articulo.idPresupuestoATNoS ??
            articulo.IdPresupuestoATNoS ??
            null,
          DescripcionArticulo: articulo.AtDescrip || null,
          analysisDisplayText: articulo.AtDescrip || null,
        });
        await this.priceDiscount_forRow(articulo, row);
      }

      let uiUpdate = {};
      switch (true) {
        case cma !== undefined:
          this.updateRowFields(row, {
            cma: true,
            lmr: false,
            trazaCultivo: false,
            CultivoOrganico: false,
          });
          uiUpdate = {
            checkCma: true,
            cmaValue: true,
            checkLmr: false,
            lmrValue: false,
            traceCultiveVisible: false,
          };
          break;
        case lmr !== undefined:
          this.updateRowFields(row, {
            cma: false,
            lmr: this.newFieldsVisibility.lmr ? true : false,
          });
          uiUpdate = {
            checkLmr: true,
            lmrValue: this.newFieldsVisibility.lmr ? true : false,
            checkCma: false,
            cmaValue: false,
            traceCultiveVisible: true,
          };
          break;
        default:
          this.updateRowFields(row, {
            cma: false,
            lmr: false,
            trazaCultivo: false,
            CultivoOrganico: false,
          });
          uiUpdate = {
            checkLmr: false,
            checkCma: false,
            cmaValue: false,
            lmrValue: false,
            traceCultiveVisible: false,
          };
      }

      const rawLegislationId = cma?.TipoServicioCma?.IdLegislacion;
      const hasValidLegislationId = Number(rawLegislationId) > 0;

      if (hasValidLegislationId) {
        await this.fetchLegislations();
        this.updateRowFields(row, {
          Legislacion: Number(rawLegislationId),
        });
        uiUpdate.legislationVisible = true;
      } else {
        this.updateRowFields(row, {
          Legislacion: null,
        });
        uiUpdate.legislationVisible = false;
      }

      this.updateRowUi(row, uiUpdate);
      this.emitMultiForm();
      this.$nextTick(() => this.refreshGrid());
    },
    onRowUpdated() {
      this.emitMultiForm();
    },

    async fetchFamilies(offer) {
      try {
        if (!this.suppressNativePreloader) {
          this.$f7.preloader.show();
        }
        await this.fetchDetallesPresupuestos(offer);
        await this.fetchFamiliesByHierarchy();
        if (
          this.detallesPresupuestos.length > 0 &&
          this.families.length > 0
        ) {
          this.offersFamiliesFiltered = [
            ...new Set(
              this.detallesPresupuestos.map((item) => item.IdFamilia)
            ),
          ];
          this.familiesFiltered = this.families.filter((item) =>
            this.offersFamiliesFiltered.includes(item.idFamiliaTM)
          );
          this.familiDisabled = false;
          this.persistExpressRegisterCache(offer);
          this.persistBudgetCacheEntry(offer);
        }
      } catch (error) {
        this.$notifyDX(
          {
            message: this.$t(
              this.$helpers.getFilteredErrorMessage(error.message)
            ),
            width: 550,
          },
          'error',
          NOTIFY_TIME_LONG
        );
      } finally {
        if (!this.suppressNativePreloader) {
          this.$f7.preloader.hide();
        }
      }
    },

    async fetchSubFamiliesFiltered_forRow(IdFamilia, row) {
      try {
        const normalizedFamilyId =
          this.normalizeSelectionId(IdFamilia);
        const normalizedBudgetCode = String(
          row?.CodPresupuesto || this.formData.CodPresupuesto || ''
        );
        const normalizedSubFamilies = [
          ...new Set(
            (this.detallesPresupuestos || [])
              .filter(
                (item) =>
                  this.normalizeSelectionId(item?.IdFamilia) ===
                  normalizedFamilyId
              )
              .map((item) =>
                this.normalizeSelectionId(
                  item?.IdSubFamilia ?? item?.IdSubFamiliaTM
                )
              )
              .filter((value) => value !== null)
          ),
        ];
        const cacheKey = `${normalizedBudgetCode}::${normalizedFamilyId}::${normalizedSubFamilies
          .map((value) => String(value))
          .sort()
          .join(',')}`;

        const cachedTypes = this.sampleTypesCacheByFamilySubFamily[
          cacheKey
        ];
        if (Array.isArray(cachedTypes) && cachedTypes.length) {
          this.updateRowUi(row, {
            sampleTypes: [...cachedTypes],
            sampleTypesDisabled: false,
          });
          this.subFamiliesFiltered = [...normalizedSubFamilies];
          return;
        }

        if (!this.suppressNativePreloader) {
          this.$f7.preloader.show();
        }
        this.subFamiliesFiltered = [...normalizedSubFamilies];
        await this.fetchSamplesTypes({
          IdFamilia,
          subfamilias: this.subFamiliesFiltered,
        });

        const loadedTypes = Array.isArray(this.sampletypes)
          ? [...this.sampletypes]
          : [];

        loadedTypes.forEach((st) => {
          this.$set(
            this.allSampleTypesMap,
            st.idTipoMuestra,
            st.Descripcion
          );
          if (Array.isArray(st.SubTiposMuestras)) {
            st.SubTiposMuestras.forEach((sub) => {
              this.$set(
                this.allSubSampleTypesMap,
                sub.idSubTipoMuestra,
                sub.Descripcion
              );
            });
          }
        });

        this.$set(
          this.sampleTypesCacheByFamilySubFamily,
          cacheKey,
          [...loadedTypes]
        );

        this.updateRowUi(row, {
          sampleTypes: loadedTypes,
          sampleTypesDisabled: false,
        });
      } catch (error) {
        this.$notifyDX(
          {
            message: this.$t(
              this.$helpers.getFilteredErrorMessage(error.message)
            ),
            width: 550,
          },
          'error',
          NOTIFY_TIME_LONG
        );
      } finally {
        if (!this.suppressNativePreloader) {
          this.$f7.preloader.hide();
        }
      }
    },

    async analysisTypeFiltered(idFamilia, idSubFamilia) {
      const normalizedFamilyId = this.normalizeSelectionId(idFamilia);
      const normalizedSubFamilyId =
        this.normalizeSelectionId(idSubFamilia);
      const normalizedBudgetCode = String(
        this.formData.CodPresupuesto || ''
      );
      const cacheKey = `${normalizedBudgetCode}::${normalizedFamilyId}::${normalizedSubFamilyId}`;

      if (
        normalizedFamilyId === null ||
        normalizedSubFamilyId === null
      ) {
        this.analysisType = [];
        return [];
      }

      const cachedAnalysisTypes =
        this.analysisTypeCacheByFamilySubFamily[cacheKey];
      if (
        Array.isArray(cachedAnalysisTypes) &&
        cachedAnalysisTypes.length
      ) {
        this.analysisType = [...cachedAnalysisTypes];
        return this.analysisType;
      }

      const matchingLines = (this.detallesPresupuestos || []).filter(
        (item) =>
          this.normalizeSelectionId(item?.IdFamilia) ===
          normalizedFamilyId &&
          this.normalizeSelectionId(
            item?.IdSubFamilia ?? item?.IdSubFamiliaTM
          ) === normalizedSubFamilyId
      );

      if (!matchingLines.length) {
        this.analysisType = [];
        return [];
      }

      this.analysisType = await this.analysisTypeDescrForLine(
        matchingLines
      );
      this.$set(
        this.analysisTypeCacheByFamilySubFamily,
        cacheKey,
        [...this.analysisType]
      );
      return this.analysisType;
    },

    getTranslatedSubFamilyLabel(item) {
      const familyId = Number(item?.IdFamilia);
      const subFamilyId = Number(
        item?.IdSubFamilia ?? item?.IdSubFamiliaTM
      );
      const family = (this.families || []).find(
        (entry) => Number(entry?.idFamiliaTM) === familyId
      );
      const subFamily = family?.SubFamiliasTM?.find(
        (entry) => Number(entry?.idSubFamiliaTM) === subFamilyId
      );

      return subFamily?.SubFamiliaTM ?? item?.SubFamiliaTM ?? '';
    },

    getAnalysisDisplayText(item) {
      if (!item) return '';

      const article =
        item.AnalisisTipo || item.Articulo || item.CodArticulo || '';
      const analysisDescription =
        item.DescripcionAT ||
        item.DescripcionArticulo ||
        item.Descripcion ||
        item.Analisis ||
        item.AnalisisTipoDescripcion ||
        '';
      const subFamilyDescription = this.getTranslatedSubFamilyLabel(
        item
      );

      return [article, analysisDescription, subFamilyDescription]
        .map((value) => (value == null ? '' : String(value).trim()))
        .filter(Boolean)
        .join(' - ');
    },

    resolveDetailLineForAnalysis(row, budgetEntry = null) {
      const resolvedRow = this.getResolvedRowData(row);
      const candidateLines = [];

      if (Array.isArray(budgetEntry?.detailLines)) {
        candidateLines.push(...budgetEntry.detailLines);
      }
      if (Array.isArray(budgetEntry?.lines)) {
        candidateLines.push(...budgetEntry.lines);
      }
      if (Array.isArray(this.detallesPresupuestos?.Lineas)) {
        candidateLines.push(...this.detallesPresupuestos.Lineas);
      }
      if (Array.isArray(this.detallesPresupuestos)) {
        candidateLines.push(...this.detallesPresupuestos);
      }

      const targetArticulo = String(
        resolvedRow?.Articulo || resolvedRow?.CodArticulo || ''
      );
      const targetIdAnalisisTipo = this.normalizeSelectionId(
        resolvedRow?.IdAnalisisTipo
      );
      const targetIdPresupuestoATNoS = this.normalizeSelectionId(
        resolvedRow?.idPresupuestoATNoS
      );
      return (
        candidateLines.find((item) => {
          const itemArticulo = String(
            item?.CodArticulo || item?.Articulo || ''
          );
          const itemIdAnalisisTipo = this.normalizeSelectionId(
            item?.IdAnalisisTipo || item?.idAnalisisTipo
          );
          const itemIdPresupuestoATNoS = this.normalizeSelectionId(
            item?.idPresupuestoATNoS || item?.IdPresupuestoATNoS
          );
          return (
            (targetArticulo &&
              itemArticulo &&
              itemArticulo === targetArticulo) ||
            (targetIdAnalisisTipo !== null &&
              itemIdAnalisisTipo !== null &&
              itemIdAnalisisTipo === targetIdAnalisisTipo) ||
            (targetIdPresupuestoATNoS !== null &&
              itemIdPresupuestoATNoS !== null &&
              itemIdPresupuestoATNoS === targetIdPresupuestoATNoS)
          );
        }) || null
      );
    },

    getAnalysisDisplayTextFromBudget(row, budgetEntry = null) {
      const resolvedRow = this.getResolvedRowData(row);
      const matchedAnalysis = this.findMatchingAnalysisForRow(
        resolvedRow,
        this.getAnalysisTypeListForRow(resolvedRow, budgetEntry)
      );

      const matchedDetailLine = this.resolveDetailLineForAnalysis(
        resolvedRow,
        budgetEntry
      );

      return (
        matchedAnalysis?.AtDescrip ||
        this.getAnalysisDisplayText(matchedAnalysis) ||
        (matchedDetailLine
          ? [
              matchedDetailLine.Articulo || matchedDetailLine.CodArticulo || '',
              matchedDetailLine.DescripcionAT || '',
              matchedDetailLine.subFamiliaIdioma || '',
            ]
              .map((value) => (value == null ? '' : String(value).trim()))
              .filter(Boolean)
              .join(' - ')
          : '') ||
        resolvedRow?.AtDescrip ||
        resolvedRow?.analysisDisplayText ||
        resolvedRow?.DescripcionArticulo ||
        ''
      );
    },

    async analysisTypeDescrForLine(OffersLines) {
      const codigoClientePresupuesto =
        this.detallesPresupuestos.codCliente;
      const [referenceLine] = OffersLines || [];

      if (!referenceLine) {
        return [];
      }

      let listadoAnalisisTipo = [];

      try {
        if (!this.suppressNativePreloader) {
          this.$f7.preloader.show();
        }
        await this.fetchAnalisisTipo({
          codPlanta:
            referenceLine.CodPlantaCentroProduccionSeleccionada || '',
          idFamiliaTM: referenceLine.IdFamilia,
          idSubFamiliaTM:
            referenceLine.IdSubFamilia ?? referenceLine.IdSubFamiliaTM,
          codCliente: codigoClientePresupuesto,
          codIdioma: null,
        });
        listadoAnalisisTipo = this.analisisTipoDescrip || [];
      } catch (error) {
        this.$notifyDX(
          {
            message: this.$t(
              this.$helpers.getFilteredErrorMessage(error.message)
            ),
            width: 550,
          },
          'error',
          NOTIFY_TIME_LONG
        );
        return [];
      } finally {
        if (!this.suppressNativePreloader) {
          this.$f7.preloader.hide();
        }
      }

      return [...OffersLines].map((item) => {
        let at = null;
        if (
          this.normalizeSelectionId(item?.IdSubFamiliaTM) === 63 ||
          this.normalizeSelectionId(item?.IdSubFamilia) === 63
        ) {
          at = listadoAnalisisTipo.find(
            (analysis) => analysis.AnalisisTipo === item.Articulo
          );
        } else {
          const idAnalisisTipo = String(item.CodArticulo || '').slice(
            1
          );
          at = listadoAnalisisTipo.find(
            (analysis) =>
              analysis.IdAnalisisTipo?.toString() === idAnalisisTipo
          );
        }
        const subFamiliaDescripcion =
          this.getTranslatedSubFamilyLabel(item);
        const analysisDescription =
          at?.DescripcionAT ?? item?.DescripcionAT ?? '';
        const analysisTypeDisplay =
          at?.AnalisisTipo ?? item?.AnalisisTipo ?? item.Articulo ?? '';

        return {
          ...item,
          CodAnalisisTipo: at?.CodAnalisisTipo ?? item?.CodAnalisisTipo,
          DescripcionAT:
            at?.IdSubFamiliaTM === 63
              ? at?.CodAnalisisTipo
              : analysisDescription,
          AtDescrip: `${analysisTypeDisplay} - ${analysisDescription} - ${subFamiliaDescripcion}`,
        };
      });
    },

    async priceDiscount_forRow(articulo, row) {
      const precio =
        articulo.PrecioBruto -
        (articulo.PrecioBruto * articulo.Descuento) / 100;
      this.updateRowFields(row, { PrecioAT: precio });
    },

    async codOfferPreSelected() {
      this.formData.CodPresupuesto = this.offerSelected;
    },

    onExpressMode(active) {
      this.expressMode = active;
    },
    onExpressGridFieldsVisibility(visibility) {
      this.newFieldsVisibility = {
        ...this.newFieldsVisibility,
        ...(visibility || {}),
      };
      this.$nextTick(() => this.refreshGrid());
    },

    ...mapActions('Offers', [
      'fetchDetallesPresupuestos',
      'fetchAnalisisTipo',
    ]),
    ...mapActions('samplesRegisterModule', [
      'fetchSamplesTypes',
      'fetchFamiliesByHierarchy',
      'fetchLegislations',
      'fetchFarmsAndParcels',
      'fetchInvoiceSamples',
      'registerInvoiceSamples',
      'updateSample',
      'deleteSample',
      'setSampleCodeInfoPopUp',
      'setConfirmPopup',
      'setConfirmExpressSamplePopUp',
      'setSamplingDate',
      'setExpressRegisterCache',
      'clearExpressRegisterCache',
      'setExpressRegisterBudgetCacheByCode',
    ]),
    ...mapActions('samplingTemplatesModule', [
      'setTemplateCreationDraft',
    ]),
  },
};
</script>

<style>
body.express-sample-register-hydrating .dx-loadpanel,
body.express-sample-register-hydrating .dx-loadpanel-wrapper,
body.express-sample-register-hydrating .dx-overlay-wrapper,
body.express-sample-register-hydrating .dx-loadpanel-content {
  display: none !important;
}

label.dx-field-item-label.dx-field-item-label-location-left {
  margin-right: -12px !important;
}
.multi-samples-title {
  margin-top: 14px;
  margin-bottom: 2px;
}
.multi-samples-title .title {
  font-weight: 700;
  font-size: 15px;
  padding: 20px 0 5px;
}
.multi-samples-title .subtitle {
  font-size: 12px;
  opacity: 0.8;
}
.CustomExpressSamplesGrid {
  margin-top: 10px;
}
.CustomExpressSamplesGrid .custom-simple-toolbar .dx-toolbar-before {
  display: grid;
  grid-template-columns: 230px 230px 230px 230px;
  grid-auto-rows: 48px;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
}

.CustomExpressSamplesGrid
  .custom-simple-toolbar
  .dx-toolbar-before
  :nth-child(1) {
  grid-row: 1 !important;
  grid-column: 1 !important;
}
.CustomExpressSamplesGrid
  .custom-simple-toolbar
  .dx-toolbar-before
  :nth-child(2) {
  grid-row: 1 !important;
  grid-column: 2 !important;
}
.CustomExpressSamplesGrid
  .custom-simple-toolbar
  .dx-toolbar-before
  :nth-child(3) {
  grid-row: 1 !important;
  grid-column: 3 !important;
}
.CustomExpressSamplesGrid
  .custom-simple-toolbar
  .dx-toolbar-before
  :nth-child(4) {
  grid-row: 1 !important;
  grid-column: 4 !important;
}
.CustomExpressSamplesGrid
  .custom-simple-toolbar
  .dx-toolbar-before
  :nth-child(5) {
  grid-row: 1 !important;
  grid-column: 5 !important;
}
.CustomExpressSamplesGrid .custom-simple-toolbar {
  padding: 15px 0px 5px !important;
}
.btn-add {
  width: 220px;
  margin: 0px 0px;
}
.btn-delete-all {
  width: 220px;
  margin: 0px 0px;
}
.btn-save-samples {
  width: 220px;
  margin: 0px 0px;
}
.btn-bulk-upload {
  width: 220px;
  margin: 0px 0px;
}
.btn-load-template {
  width: 220px;
  margin: 0px 0px;
}

.CustomExpressSamplesGrid .express-sample-row--registered > td {
  background-color: #e6f6ea !important;
}

.CustomSimplesSamplesGrid
  .express-sample-row--registered-editing
  > td {
  background-color: #dceaf8 !important;
}
.express-grid-wrapper {
  position: relative;
}
.express-grid-wrapper--hydrating .dx-loadpanel,
.express-grid-wrapper--hydrating .dx-overlay-wrapper,
.express-grid-wrapper--hydrating .dx-loadpanel-content {
  display: none !important;
}
.express-grid-loading {
  min-height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.express-analysis-select-dropdown {
  z-index: 9999 !important;
}

.express-analysis-select-dropdown .dx-overlay-content {
  min-width: 620px !important;
  max-width: 80vw !important;
}

.express-analysis-select-dropdown .dx-list-item-content {
  white-space: normal !important;
  overflow: visible !important;
  text-overflow: unset !important;
  line-height: 16px !important;
  padding-top: 8px !important;
  padding-bottom: 8px !important;
}
.express-analysis-select-dropdown .dx-list-item {
  background-color: #ffffff !important;
  color: #00346a !important;
}

.express-analysis-select-dropdown .dx-list-item.dx-state-hover,
.express-analysis-select-dropdown .dx-list-item.dx-state-focused,
.express-analysis-select-dropdown
  .dx-list-item.dx-list-item-selected {
  background-color: #ffffff !important;
  color: #00346a !important;
}

.express-analysis-select-dropdown
  .dx-list-item.dx-state-hover
  .dx-list-item-content,
.express-analysis-select-dropdown
  .dx-list-item.dx-state-focused
  .dx-list-item-content,
.express-analysis-select-dropdown
  .dx-list-item.dx-list-item-selected
  .dx-list-item-content {
  background-color: #ffffff !important;
  color: #00346a !important;
}
.express-budget-select-dropdown {
  z-index: 9999 !important;
}

.express-budget-select-dropdown .dx-overlay-content {
  min-width: 620px !important;
  max-width: 80vw !important;
}

.express-budget-select-dropdown .dx-list-item-content {
  white-space: normal !important;
  overflow: visible !important;
  text-overflow: unset !important;
  line-height: 16px !important;
  padding-top: 8px !important;
  padding-bottom: 8px !important;
}

.express-budget-select-dropdown .dx-list-item {
  background-color: #ffffff !important;
  color: #00346a !important;
}

.express-budget-select-dropdown .dx-list-item.dx-state-hover,
.express-budget-select-dropdown .dx-list-item.dx-state-focused,
.express-budget-select-dropdown .dx-list-item.dx-list-item-selected {
  background-color: #ffffff !important;
  color: #00346a !important;
}

.express-budget-select-dropdown
  .dx-list-item.dx-state-hover
  .dx-list-item-content,
.express-budget-select-dropdown
  .dx-list-item.dx-state-focused
  .dx-list-item-content,
.express-budget-select-dropdown
  .dx-list-item.dx-list-item-selected
  .dx-list-item-content {
  background-color: #ffffff !important;
  color: #00346a !important;
}
.express-subtype-select-dropdown {
  z-index: 9999 !important;
}

.express-subtype-select-dropdown .dx-overlay-content {
  min-width: 420px !important;
  max-width: 70vw !important;
}

.express-subtype-select-dropdown .dx-list-item-content {
  white-space: normal !important;
  overflow: visible !important;
  text-overflow: unset !important;
  line-height: 16px !important;
  padding-top: 8px !important;
  padding-bottom: 8px !important;
}

.express-subtype-select-dropdown .dx-list-item {
  background-color: #ffffff !important;
  color: #00346a !important;
}

.express-subtype-select-dropdown .dx-list-item.dx-state-hover,
.express-subtype-select-dropdown .dx-list-item.dx-state-focused,
.express-subtype-select-dropdown .dx-list-item.dx-list-item-selected {
  background-color: #ffffff !important;
  color: #00346a !important;
}

.express-subtype-select-dropdown
  .dx-list-item.dx-state-hover
  .dx-list-item-content,
.express-subtype-select-dropdown
  .dx-list-item.dx-state-focused
  .dx-list-item-content,
.express-subtype-select-dropdown
  .dx-list-item.dx-list-item-selected
  .dx-list-item-content {
  background-color: #ffffff !important;
  color: #00346a !important;
}

.CustomExpressSamplesGrid .dx-button-has-text .dx-icon {
  line-height: 1.2;
}

.express-sample-register--hydrating {
  position: relative;
  min-height: 560px;
}

.express-register-preloader-overlay {
  position: absolute;
  inset: 0;

  width: 100%;
  min-height: 560px;

  display: flex;
  align-items: center;
  justify-content: center;

  z-index: 20;
  background: transparent;
}

.express-register-preloader-overlay > .preloader-card {
  background: #ffffff !important;
  border-radius: 12px !important;

  width: 212px !important;
  height: 114px !important;
  min-width: 190px !important;
  min-height: 92px !important;
  padding: 0 !important;
  box-sizing: border-box !important;

  display: flex !important;
  flex-direction: column !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 10px !important;

  box-shadow: 0px 6px 20px rgba(0, 0, 0, 0.15) !important;

  transform: translateY(32px);
}

.express-register-preloader-overlay .preloader {
  --f7-preloader-color: #5f6368;
  width: 30px !important;
  height: 30px !important;
}

.express-register-preloader-overlay .preloader-text {
  font-size: 14px;
  font-weight: 500;
  color: #333333;
}

.required-header-asterisk {
  color: #d9534f;
  margin-left: 4px;
}

.express-farmPoint-select-dropdown {
  z-index: 9999 !important;
}

.express-farmPoint-select-dropdown .dx-overlay-content {
  min-width: 620px !important;
  max-width: 80vw !important;
}

.express-farmPoint-select-dropdown .dx-list-item-content {
  white-space: normal !important;
  overflow: visible !important;
  text-overflow: unset !important;
  line-height: 16px !important;
  padding-top: 8px !important;
  padding-bottom: 8px !important;
}
.express-farmPoint-select-dropdown .dx-list-item {
  background-color: #ffffff !important;
  color: #00346a !important;
}

.express-farmPoint-select-dropdown .dx-list-item.dx-state-hover,
.express-farmPoint-select-dropdown .dx-list-item.dx-state-focused,
.express-farmPoint-select-dropdown
  .dx-list-item.dx-list-item-selected {
  background-color: #ffffff !important;
  color: #00346a !important;
}

.express-farmPoint-select-dropdown
  .dx-list-item.dx-state-hover
  .dx-list-item-content,
.express-farmPoint-select-dropdown
  .dx-list-item.dx-state-focused
  .dx-list-item-content,
.express-farmPoint-select-dropdown
  .dx-list-item.dx-list-item-selected
  .dx-list-item-content {
  background-color: #ffffff !important;
  color: #00346a !important;
}

.express-parcelPoint-select-dropdown {
  z-index: 9999 !important;
}

.express-parcelPoint-select-dropdown .dx-overlay-content {
  min-width: 620px !important;
  max-width: 80vw !important;
}

.express-parcelPoint-select-dropdown .dx-list-item-content {
  white-space: normal !important;
  overflow: visible !important;
  text-overflow: unset !important;
  line-height: 16px !important;
  padding-top: 8px !important;
  padding-bottom: 8px !important;
}
.express-parcelPoint-select-dropdown .dx-list-item {
  background-color: #ffffff !important;
  color: #00346a !important;
}

.express-parcelPoint-select-dropdown .dx-list-item.dx-state-hover,
.express-parcelPoint-select-dropdown .dx-list-item.dx-state-focused,
.express-parcelPoint-select-dropdown
  .dx-list-item.dx-list-item-selected {
  background-color: #ffffff !important;
  color: #00346a !important;
}

.express-parcelPoint-select-dropdown
  .dx-list-item.dx-state-hover
  .dx-list-item-content,
.express-parcelPoint-select-dropdown
  .dx-list-item.dx-state-focused
  .dx-list-item-content,
.express-parcelPoint-select-dropdown
  .dx-list-item.dx-list-item-selected
  .dx-list-item-content {
  background-color: #ffffff !important;
  color: #00346a !important;
}

.express-levelPoint-select-dropdown {
  z-index: 9999 !important;
}

.express-levelPoint-select-dropdown .dx-overlay-content {
  min-width: 620px !important;
  max-width: 80vw !important;
}

.express-levelPoint-select-dropdown .dx-list-item-content {
  white-space: normal !important;
  overflow: visible !important;
  text-overflow: unset !important;
  line-height: 16px !important;
  padding-top: 8px !important;
  padding-bottom: 8px !important;
}
.express-levelPoint-select-dropdown .dx-list-item {
  background-color: #ffffff !important;
  color: #00346a !important;
}

.express-levelPoint-select-dropdown .dx-list-item.dx-state-hover,
.express-levelPoint-select-dropdown .dx-list-item.dx-state-focused,
.express-levelPoint-select-dropdown
  .dx-list-item.dx-list-item-selected {
  background-color: #ffffff !important;
  color: #00346a !important;
}

.express-levelPoint-select-dropdown
  .dx-list-item.dx-state-hover
  .dx-list-item-content,
.express-levelPoint-select-dropdown
  .dx-list-item.dx-state-focused
  .dx-list-item-content,
.express-levelPoint-select-dropdown
  .dx-list-item.dx-list-item-selected
  .dx-list-item-content {
  background-color: #ffffff !important;
  color: #00346a !important;
}
</style>
